import api, { proxyAvatarUrl, proxyGooglePhotoUrl } from './api';
import { INBOUND_REPLY_TEMPLATES, InboundReplyTemplate } from '../constants/inboundReplyTemplates';
import {
  InboxCategoryConfig,
  getInboxCategoryConfigFor,
  setInboxCategoryConfigCache,
} from '../utils/inboxCategoryConfig';

const asObject = (value: any): Record<string, any> => (value && typeof value === 'object' ? value : {});
const firstString = (...values: any[]): string => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const next = String(value).trim();
    if (next) return next;
  }
  return '';
};
const firstNullableString = (...values: any[]): string | null => {
  const value = firstString(...values);
  return value || null;
};
const toArray = <T = any>(value: any): T[] => (Array.isArray(value) ? value : []);
const toNumberOr = (value: any, fallback: number): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};
const toBoolean = (value: any, fallback = false): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return fallback;
};
const firstNumber = (...values: any[]): number | undefined => {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num) && num >= 0) return num;
  }
  return undefined;
};

const GMAIL_SYSTEM_STATE_LABELS = new Set([
  'INBOX',
  'SENT',
  'DRAFT',
  'UNREAD',
  'STARRED',
  'IMPORTANT',
  'SPAM',
  'TRASH',
]);

const isHiddenGmailSystemStateLabel = (value: any): boolean => {
  const normalized = String(value || '').trim().toUpperCase();
  if (!normalized) return false;
  return GMAIL_SYSTEM_STATE_LABELS.has(normalized) || normalized.startsWith('CATEGORY_');
};

const toDisplayableLabels = (value: any): string[] =>
  toArray<string>(value)
    .map((entry) => String(entry || '').trim())
    .filter(Boolean)
    .filter((label) => !isHiddenGmailSystemStateLabel(label));
const normalizeCaseState = (value: any): IntakeCaseState | null => {
  const normalized = String(value || '').trim().toUpperCase();
  if (!normalized) return null;
  if (['OPEN', 'WAITING_CLIENT', 'RESOLVED_WON', 'RESOLVED_LOST', 'NO_ACTION'].includes(normalized)) {
    return normalized as IntakeCaseState;
  }
  return null;
};
const normalizeCategory = (value: any): IntakeCategory => {
  return getInboxCategoryConfigFor(value).key as IntakeCategory;
};
const normalizeSlaLevel = (value: any): IntakeSlaLevel | null => {
  const normalized = String(value || '').trim().toUpperCase();
  if (normalized === 'GREEN' || normalized === 'AMBER' || normalized === 'RED') {
    return normalized as IntakeSlaLevel;
  }
  return null;
};
const normalizePaymentBadgeTone = (value: any): 'GREEN' | 'RED' | 'ORANGE' | 'GRAY' => {
  const normalized = String(value || '').trim().toUpperCase();
  if (normalized === 'GREEN' || normalized === 'RED' || normalized === 'ORANGE') {
    return normalized as 'GREEN' | 'RED' | 'ORANGE';
  }
  return 'GRAY';
};
const normalizeQueueItem = (raw: any): IntakeQueueItem => {
  const item = asObject(raw);
  return {
    id: firstString(item.id),
    threadId: firstString(item.threadId, item.emailThreadId, item.conversationId),
    customerId: firstNullableString(item.customerId),
    contactName: firstNullableString(item.contactName),
    contactCompany: firstNullableString(item.contactCompany),
    contactMatchConfidence: firstNullableString(item.contactMatchConfidence),
    contactMatchReason: firstNullableString(item.contactMatchReason),
    paymentPolicyType: firstNullableString(item.paymentPolicyType),
    paymentPolicyLabel: firstNullableString(item.paymentPolicyLabel),
    creditStatus: firstNullableString(item.creditStatus),
    requiresPrepayment: toBoolean(item.requiresPrepayment, false),
    bookingBlockMode: firstNullableString(item.bookingBlockMode),
    paymentPolicyNotes: firstNullableString(item.paymentPolicyNotes),
    paymentWarningLabel: firstNullableString(item.paymentWarningLabel),
    paymentBadgeTone: normalizePaymentBadgeTone(item.paymentBadgeTone),
    paymentBadgeLabel: firstNullableString(item.paymentBadgeLabel),
    contactLinkSuppressed: toBoolean(item.contactLinkSuppressed, false),
    contactLinkMode: firstNullableString(item.contactLinkMode),
    participantSummary: firstNullableString(item.participantSummary),
    participantLabels: toArray<string>(item.participantLabels).map(value => String(value || '').trim()).filter(Boolean),
    participantDetails: toArray<any>(item.participantDetails).map((entry) => {
      const row = asObject(entry);
      return {
        label: firstString(row.label),
        email: firstNullableString(row.email),
        kind: firstString(row.kind, 'EXTERNAL') as 'EXTERNAL' | 'INTERNAL' | 'SELF',
        customerId: firstNullableString(row.customerId),
        company: firstNullableString(row.company),
      };
    }).filter((entry) => !!entry.label),
    receivedAt: firstString(item.receivedAt, item.createdAt),
    createdAt: firstString(item.createdAt, item.receivedAt),
    subject: firstString(item.subject),
    fromEmail: firstString(item.fromEmail),
    toEmails: firstString(item.toEmails),
    category: normalizeCategory(item.category),
    confidence: toNumberOr(item.confidence, 0),
    flightRelated: toBoolean(item.flightRelated, false),
    reasons: toArray<string>(item.reasons).map(value => String(value || '').trim()).filter(Boolean),
    labels: toDisplayableLabels(item.labels),
    inboxKey: firstString(item.inboxKey),
    inboxKeys: toArray<string>(item.inboxKeys).map(value => String(value || '').trim().toLowerCase()).filter(Boolean),
    isWatched: toBoolean(item.isWatched, false),
    hasReply: toBoolean(item.hasReply, false),
    agentDraftEligible: toBoolean(item.agentDraftEligible, false),
    linkedQuoteId: firstNullableString(item.linkedQuoteId, item.quoteId),
    linkedQuoteNumber: firstNullableString(item.linkedQuoteNumber, item.quoteNumber),
    linkedQuoteMode: firstNullableString(item.linkedQuoteMode, item.productMode),
    linkedQuoteStatus: firstNullableString(item.linkedQuoteStatus, item.quoteStatus),
    convertedAt: firstNullableString(item.convertedAt),
    convertedByName: firstNullableString(item.convertedByName),
    convertedByEmail: firstNullableString(item.convertedByEmail),
    repliedAt: firstNullableString(item.repliedAt),
    repliedBy: firstNullableString(item.repliedBy),
    replySource: firstNullableString(item.replySource),
    replyDeliveryStatus: firstNullableString(item.replyDeliveryStatus),
    replyDeliveryAt: firstNullableString(item.replyDeliveryAt),
    replyDeliveryError: firstNullableString(item.replyDeliveryError),
    assignedToUserId: firstNullableString(item.assignedToUserId),
    assignedToName: firstNullableString(item.assignedToName),
    assignedToEmail: firstNullableString(item.assignedToEmail),
    assignedAt: firstNullableString(item.assignedAt),
    assignedByName: firstNullableString(item.assignedByName),
    caseState: normalizeCaseState(item.caseState),
    archived: toBoolean(item.archived, false),
    gmailState: firstNullableString(item.gmailState),
    gmailDeletedAt: firstNullableString(item.gmailDeletedAt),
    gmailMissingAt: firstNullableString(item.gmailMissingAt),
    verificationStatus: firstNullableString(item.verificationStatus),
    verificationAt: firstNullableString(item.verificationAt),
    verifiedByName: firstNullableString(item.verifiedByName),
    verifiedByEmail: firstNullableString(item.verifiedByEmail),
    slaAgeMinutes: Number.isFinite(Number(item.slaAgeMinutes)) ? Number(item.slaAgeMinutes) : null,
    slaLevel: normalizeSlaLevel(item.slaLevel),
    isRead: toBoolean(item.isRead, false),
    attachmentCount: toNumberOr(item.attachmentCount, 0),
    noteCount: toNumberOr(item.noteCount, 0),
    latestNoteAt: firstNullableString(item.latestNoteAt),
    latestNotePreview: firstNullableString(item.latestNotePreview),
    latestNoteAuthor: firstNullableString(item.latestNoteAuthor),
    unreadNoteCount: toNumberOr(item.unreadNoteCount, 0),
    followUpTaskId: firstNullableString(item.followUpTaskId),
    followUpDueAt: firstNullableString(item.followUpDueAt),
    followUpOwnerUserId: firstNullableString(item.followUpOwnerUserId),
    followUpOwnerName: firstNullableString(item.followUpOwnerName),
    followUpNote: firstNullableString(item.followUpNote),
    followUpStatus: firstNullableString(item.followUpStatus),
    followUpCompletedAt: firstNullableString(item.followUpCompletedAt),
    messageCount: toNumberOr(item.messageCount, 1),
    unreadMessageCount: toNumberOr(item.unreadMessageCount, 0),
    readByUsers: Array.isArray(item.readByUsers)
      ? item.readByUsers.map((r: any) => ({
          userId: String(r.userId || ''),
          userName: r.userName ? String(r.userName) : null,
          readAt: String(r.readAt || ''),
        }))
      : [],
    lastActivity: item.lastActivity && typeof item.lastActivity === 'object'
      ? {
          action: String(item.lastActivity.action || ''),
          by: item.lastActivity.by ? String(item.lastActivity.by) : null,
          at: typeof item.lastActivity.at === 'number'
              ? new Date(item.lastActivity.at).toISOString()
              : String(item.lastActivity.at || ''),
        }
      : null,
    reconfirmation: item.reconfirmation && typeof item.reconfirmation === 'object'
      ? {
          status: String(item.reconfirmation.status || ''),
          scheduledAt: firstNullableString(item.reconfirmation.scheduledAt),
          flightDate: firstNullableString(item.reconfirmation.flightDate),
          firstSentAt: firstNullableString(item.reconfirmation.firstSentAt),
          secondSentAt: firstNullableString(item.reconfirmation.secondSentAt),
          clientRepliedAt: firstNullableString(item.reconfirmation.clientRepliedAt),
        }
      : null,
    provisional: item.provisional && typeof item.provisional === 'object'
      ? {
          status: String(item.provisional.status || ''),
          badgeState: item.provisional.badgeState ? String(item.provisional.badgeState) : null,
          firstFollowUpAt: firstNullableString(item.provisional.firstFollowUpAt),
          firstActionedAt: firstNullableString(item.provisional.firstActionedAt),
          secondFollowUpAt: firstNullableString(item.provisional.secondFollowUpAt),
          secondActionedAt: firstNullableString(item.provisional.secondActionedAt),
          clientRepliedAt: firstNullableString(item.provisional.clientRepliedAt),
        }
      : null,
    provisionalHint: item.provisionalHint === true,
  };
};
const normalizeAlertItem = (raw: any): IntakeAlertItem => {
  const item = asObject(raw);
  const channel = String(item.channel || '').trim().toUpperCase();
  return {
    messageId: firstString(item.messageId, item.id),
    threadId: firstString(item.threadId, item.emailThreadId),
    category: normalizeCategory(item.category),
    subject: firstString(item.subject),
    fromEmail: firstString(item.fromEmail),
    receivedAt: firstString(item.receivedAt),
    ageMinutes: toNumberOr(item.ageMinutes, 0),
    escalationLevel: toNumberOr(item.escalationLevel, 0),
    notifyRoles: toArray<string>(item.notifyRoles).map(v => String(v || '').trim()).filter(Boolean),
    notifyEmails: toArray<string>(item.notifyEmails).map(v => String(v || '').trim()).filter(Boolean),
    channel: channel === 'EMAIL' || channel === 'BOTH' ? (channel as 'EMAIL' | 'BOTH') : 'IN_APP',
  };
};
const normalizeMessageTimeline = (raw: any) => {
  const item = asObject(raw);
  const rawAvatarUrl = firstNullableString(item.contactAvatarUrl);
  const rawActorPhotoUrl = firstNullableString(item.actorPhotoUrl);
  return {
    id: firstString(item.id),
    direction: firstString(item.direction, 'UNKNOWN'),
    status: firstString(item.status),
    sourceLabel: firstNullableString(item.sourceLabel) || undefined,
    repliedBy: firstNullableString(item.repliedBy) || undefined,
    subject: firstString(item.subject),
    fromEmail: firstString(item.fromEmail),
    contactAvatarUrl: rawAvatarUrl ? proxyGooglePhotoUrl(rawAvatarUrl, `${firstString(item.id)}:${rawAvatarUrl}`) : undefined,
    actorPhotoUrl: rawActorPhotoUrl ? proxyAvatarUrl(rawActorPhotoUrl) : undefined,
    fromName: firstNullableString(item.fromName) || undefined,
    toEmails: firstString(item.toEmails),
    ccEmails: firstNullableString(item.ccEmails) || undefined,
    bccEmails: firstNullableString(item.bccEmails) || undefined,
    replyToEmail: firstNullableString(item.replyToEmail) || undefined,
    textBody: firstString(item.textBody),
    htmlBody: firstString(item.htmlBody),
    attachments: toArray<any>(item.attachments).map(entry => {
      const attachment = asObject(entry);
      const sizeBytes = Number(attachment.sizeBytes);
      return {
        attachmentId: firstString(attachment.attachmentId, attachment.id),
        fileName: firstString(attachment.fileName, attachment.filename, attachment.name),
        mimeType: firstNullableString(attachment.mimeType, attachment.contentType, attachment.type),
        sizeBytes: Number.isFinite(sizeBytes) ? sizeBytes : null,
        url: firstNullableString(attachment.url, attachment.downloadUrl, attachment.href),
        canDownload: toBoolean(attachment.canDownload, false),
        contentId: firstNullableString(attachment.contentId, attachment.cid),
        isInline: toBoolean(attachment.isInline, false),
        dataBase64: firstNullableString(attachment.dataBase64) || null,
        gmailId: firstNullableString(attachment.gmailId),
      };
    }).filter(file => !!file.fileName),
    createdAt: firstString(item.createdAt),
    receivedAt: firstNullableString(item.receivedAt) || undefined,
    sentAt: firstNullableString(item.sentAt) || undefined,
  };
};
const normalizeMessageDetail = (raw: any): IntakeMessageDetail => {
  const data = asObject(raw);
  const message = asObject(data.message || data.Message || data.emailMessage);
  const thread = asObject(data.thread || data.Thread || data.emailThread);
  const threadMessages = toArray<any>(thread.messages || thread.Messages).map(normalizeMessageTimeline);
  return {
    message: {
      id: firstString(message.id),
      threadId: firstString(message.threadId, message.emailThreadId, thread.id),
      customerId: firstNullableString(message.customerId, thread.customerId),
      contactName: firstNullableString(message.contactName, thread.contactName),
      contactCompany: firstNullableString(message.contactCompany, thread.contactCompany),
      contactMatchConfidence: firstNullableString(message.contactMatchConfidence),
      contactMatchReason: firstNullableString(message.contactMatchReason),
      paymentPolicyType: firstNullableString(message.paymentPolicyType, thread.paymentPolicyType),
      paymentPolicyLabel: firstNullableString(message.paymentPolicyLabel, thread.paymentPolicyLabel),
      creditStatus: firstNullableString(message.creditStatus, thread.creditStatus),
      requiresPrepayment: toBoolean(message.requiresPrepayment ?? thread.requiresPrepayment, false),
      bookingBlockMode: firstNullableString(message.bookingBlockMode, thread.bookingBlockMode),
      paymentPolicyNotes: firstNullableString(message.paymentPolicyNotes, thread.paymentPolicyNotes),
      paymentWarningLabel: firstNullableString(message.paymentWarningLabel, thread.paymentWarningLabel),
      paymentBadgeTone: normalizePaymentBadgeTone(message.paymentBadgeTone || thread.paymentBadgeTone),
      paymentBadgeLabel: firstNullableString(message.paymentBadgeLabel, thread.paymentBadgeLabel),
      contactLinkSuppressed: toBoolean(message.contactLinkSuppressed, false),
      contactLinkMode: firstNullableString(message.contactLinkMode),
      subject: firstString(message.subject),
      fromEmail: firstString(message.fromEmail),
      toEmails: firstString(message.toEmails),
      ccEmails: firstString(message.ccEmails),
      bccEmails: firstString(message.bccEmails),
      textBody: firstString(message.textBody),
      htmlBody: firstString(message.htmlBody),
      receivedAt: firstString(message.receivedAt, message.createdAt),
      rawPayload: message.rawPayload || null,
      linkedQuoteId: firstNullableString(message.linkedQuoteId, message.quoteId),
      linkedQuoteNumber: firstNullableString(message.linkedQuoteNumber, message.quoteNumber),
      linkedQuoteMode: firstNullableString(message.linkedQuoteMode, message.productMode),
      linkedQuoteStatus: firstNullableString(message.linkedQuoteStatus, message.quoteStatus),
      category: normalizeCategory(message.category),
      contactAvatarUrl: (() => {
        const rawAvatarUrl = firstNullableString(message.contactAvatarUrl);
        return rawAvatarUrl ? proxyGooglePhotoUrl(rawAvatarUrl, `${firstString(message.id)}:${rawAvatarUrl}`) : null;
      })(),
      confidence: Number.isFinite(Number(message.confidence)) ? Number(message.confidence) : undefined,
      flightRelated: toBoolean(message.flightRelated, false),
      reasons: toArray<string>(message.reasons).map(v => String(v || '').trim()).filter(Boolean),
      caseState: normalizeCaseState(message.caseState),
      archived: toBoolean(message.archived, false),
      gmailState: firstNullableString(message.gmailState),
      gmailDeletedAt: firstNullableString(message.gmailDeletedAt),
      gmailMissingAt: firstNullableString(message.gmailMissingAt),
      gmailLastSeenAt: firstNullableString(message.gmailLastSeenAt),
      verificationStatus: firstNullableString(message.verificationStatus),
      verificationAt: firstNullableString(message.verificationAt),
      verifiedByName: firstNullableString(message.verifiedByName),
      verifiedByEmail: firstNullableString(message.verifiedByEmail),
      assignedToUserId: firstNullableString(message.assignedToUserId, thread.assignedToUserId),
      assignedToName: firstNullableString(message.assignedToName, thread.assignedToName),
      assignedToEmail: firstNullableString(message.assignedToEmail, thread.assignedToEmail),
      assignedAt: firstNullableString(message.assignedAt, thread.assignedAt),
      assignedByName: firstNullableString(message.assignedByName),
      convertedAt: firstNullableString(message.convertedAt),
      convertedByName: firstNullableString(message.convertedByName),
      convertedByEmail: firstNullableString(message.convertedByEmail),
      repliedAt: firstNullableString(message.repliedAt),
      repliedBy: firstNullableString(message.repliedBy),
      replySource: firstNullableString(message.replySource),
      labels: toDisplayableLabels(message.labels ?? (message.rawPayload || {}).labels),
      missingFields: toArray<string>(message.missingFields).map(v => String(v || '').trim().toUpperCase()).filter(Boolean),
      missingQuestions: toArray<string>(message.missingQuestions).map(v => String(v || '').trim()).filter(Boolean),
      forwardedRouting: {
        detected: toBoolean((message.forwardedRouting || {}).detected, false),
        originalSenderEmail: firstNullableString((message.forwardedRouting || {}).originalSenderEmail),
        forwarderEmail: firstNullableString((message.forwardedRouting || {}).forwarderEmail),
        suggestedCc: toArray<string>((message.forwardedRouting || {}).suggestedCc).map(v => String(v || '').trim()).filter(Boolean),
      },
      replyMailboxOptions: toArray<string>(message.replyMailboxOptions).map(v => String(v || '').trim().toLowerCase()).filter(Boolean),
      replyMailboxDefaultKey: firstNullableString(message.replyMailboxDefaultKey),
      followUp: message.followUp && typeof message.followUp === 'object'
        ? {
            taskId: firstNullableString(message.followUp.taskId),
            dueAt: firstNullableString(message.followUp.dueAt),
            ownerUserId: firstNullableString(message.followUp.ownerUserId),
            ownerName: firstNullableString(message.followUp.ownerName),
            note: firstNullableString(message.followUp.note),
            status: firstNullableString(message.followUp.status),
            completedAt: firstNullableString(message.followUp.completedAt),
          }
        : null,
    },
    thread: {
      id: firstString(thread.id, message.threadId, message.emailThreadId),
      customerId: firstNullableString(thread.customerId, message.customerId),
      contactName: firstNullableString(thread.contactName, message.contactName),
      contactCompany: firstNullableString(thread.contactCompany, message.contactCompany),
      paymentPolicyType: firstNullableString(thread.paymentPolicyType, message.paymentPolicyType),
      paymentPolicyLabel: firstNullableString(thread.paymentPolicyLabel, message.paymentPolicyLabel),
      creditStatus: firstNullableString(thread.creditStatus, message.creditStatus),
      requiresPrepayment: toBoolean(thread.requiresPrepayment ?? message.requiresPrepayment, false),
      bookingBlockMode: firstNullableString(thread.bookingBlockMode, message.bookingBlockMode),
      paymentPolicyNotes: firstNullableString(thread.paymentPolicyNotes, message.paymentPolicyNotes),
      paymentWarningLabel: firstNullableString(thread.paymentWarningLabel, message.paymentWarningLabel),
      paymentBadgeTone: normalizePaymentBadgeTone(thread.paymentBadgeTone || message.paymentBadgeTone),
      paymentBadgeLabel: firstNullableString(thread.paymentBadgeLabel, message.paymentBadgeLabel),
      contactLinkSuppressed: toBoolean(thread.contactLinkSuppressed, message.contactLinkSuppressed, false),
      contactLinkMode: firstNullableString(thread.contactLinkMode, message.contactLinkMode),
      contactAvatarUrl: (() => {
        const rawAvatarUrl = firstNullableString(thread.contactAvatarUrl, message.contactAvatarUrl);
        return rawAvatarUrl ? proxyGooglePhotoUrl(rawAvatarUrl, `${firstString(thread.id, message.threadId)}:${rawAvatarUrl}`) : null;
      })(),
      customerEmail: firstString(thread.customerEmail),
      status: firstString(thread.status, 'OPEN'),
      assignedToUserId: firstNullableString(thread.assignedToUserId, message.assignedToUserId),
      assignedToName: firstNullableString(thread.assignedToName, message.assignedToName),
      assignedToEmail: firstNullableString(thread.assignedToEmail, message.assignedToEmail),
      assignedAt: firstNullableString(thread.assignedAt, message.assignedAt),
      messages: threadMessages,
    },
    auditTimeline: toArray<any>(data.auditTimeline).map(entry => {
      const item = asObject(entry);
      return {
        id: firstString(item.id),
        at: firstString(item.at, item.timestamp),
        action: firstString(item.action),
        by: firstString(item.by, item.userEmail, 'system'),
        changes: item.changes || null,
      };
    }),
  };
};
const normalizeReplyAction = (value: any): EmailReplyAction => {
  const normalized = String(value || '').trim().toUpperCase();
  if (['ASK_INFO', 'SEND_OFFER', 'FOLLOW_UP', 'VERIFY_CLIENT', 'CUSTOM'].includes(normalized)) {
    return normalized as EmailReplyAction;
  }
  return 'CUSTOM';
};
const normalizeReplyScenario = (value: any): EmailReplyScenario => {
  const normalized = String(value || '').trim().toUpperCase();
  if (
    [
      'ANSWER_AND_GUIDE',
      'ASK_MISSING_INFO',
      'ACKNOWLEDGE_AND_REVIEW',
      'SEND_OFFER',
      'FOLLOW_UP',
      'VERIFY_CLIENT',
      'CONFIRM_NEXT_STEP',
      'CUSTOM_REPLY',
    ].includes(normalized)
  ) {
    return normalized as EmailReplyScenario;
  }
  return 'CUSTOM_REPLY';
};
const normalizeReplyPriority = (value: any): EmailReplyPriority => {
  const normalized = String(value || '').trim().toUpperCase();
  if (['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].includes(normalized)) {
    return normalized as EmailReplyPriority;
  }
  return 'MEDIUM';
};
const normalizeReplyIntelligence = (raw: any): EmailReplyIntelligenceResponse => {
  const item = asObject(raw);
  const decision = asObject(item.decisionBar);
  const suggestion = item.suggestion && typeof item.suggestion === 'object' ? asObject(item.suggestion) : null;
  const templateUsed = suggestion?.templateUsed && typeof suggestion.templateUsed === 'object'
    ? asObject(suggestion.templateUsed)
    : null;
  return {
    decisionBar: {
      scenario: normalizeReplyScenario(decision.scenario),
      intentType: firstString(decision.intentType, 'UNCLEAR') as EmailReplyIntentType,
      intentLabel: firstString(decision.intentLabel, 'Custom Reply'),
      intentConfidence: Number.isFinite(Number(decision.intentConfidence)) ? Number(decision.intentConfidence) : 0.5,
      intentExplanation: firstNullableString(decision.intentExplanation),
      missingFields: toArray<string>(decision.missingFields).map(v => String(v || '').trim()).filter(Boolean),
      priorityLabel: normalizeReplyPriority(decision.priorityLabel),
      recommendedAction: normalizeReplyAction(decision.recommendedAction),
      followUpState: firstString(decision.followUpState, 'NONE') as EmailReplyFollowUpState,
      verificationState: firstString(decision.verificationState, 'NOT_REQUIRED') as EmailReplyVerificationState,
      quoteState: firstString(decision.quoteState, 'NONE') as EmailReplyQuoteState,
      explanation: firstString(decision.explanation),
    },
    suggestedModes: toArray<any>(item.suggestedModes).map(entry => ({
      key: normalizeReplyAction(entry?.key),
      label: firstString(entry?.label) || (normalizeReplyAction(entry?.key) as string).replace(/_/g, ' '),
      recommended: toBoolean(entry?.recommended, false),
    })),
    suggestionAvailable: toBoolean(item.suggestionAvailable, false),
    copilotEnabled: toBoolean(item.copilotEnabled, false),
    aiRefineAvailable: toBoolean(item.aiRefineAvailable, false),
    reasonUnavailable: firstNullableString(item.reasonUnavailable),
    suggestion: suggestion
      ? {
          subject: firstString(suggestion.subject),
          body: firstString(suggestion.body),
          modeUsed: normalizeReplyAction(suggestion.modeUsed),
          source: firstString(suggestion.source, 'TEMPLATE_ONLY') as 'TEMPLATE_ONLY' | 'TEMPLATE_PLUS_AI',
          templateUsed: templateUsed
            ? {
                key: firstNullableString(templateUsed.key),
                title: firstNullableString(templateUsed.title),
                scenario: normalizeReplyScenario(templateUsed.scenario),
                tone: firstNullableString(templateUsed.tone),
                source: firstString(templateUsed.source, 'SYSTEM_FALLBACK') as 'SUPERVISOR_TEMPLATE' | 'SYSTEM_FALLBACK',
              }
            : undefined,
          sourceComponents: Array.isArray(suggestion.sourceComponents)
            ? suggestion.sourceComponents.map((v: any) => String(v || '').trim()).filter(Boolean)
            : [],
          goldExamplesUsed: toBoolean(suggestion.goldExamplesUsed, false),
          goldExampleCount: Number.isFinite(Number(suggestion.goldExampleCount)) ? Number(suggestion.goldExampleCount) : 0,
          goldExampleTitles: Array.isArray(suggestion.goldExampleTitles)
            ? suggestion.goldExampleTitles.map((v: any) => String(v || '').trim()).filter(Boolean)
            : [],
          offerReady: toBoolean(suggestion.offerReady, false),
          warnings: toArray<string>(suggestion.warnings).map(v => String(v || '').trim()).filter(Boolean),
          confidence: Number.isFinite(Number(suggestion.confidence)) ? Number(suggestion.confidence) : 0.5,
          suggestionLogId: firstNullableString(suggestion.suggestionLogId),
        }
      : null,
  };
};
const normalizeQuoteSearchItem = (raw: any): IntakeQuoteSearchItem => {
  const item = asObject(raw);
  return {
    id: firstString(item.id),
    quoteNumber: firstNullableString(item.quoteNumber),
    bookingId: firstNullableString(item.bookingId),
    pnrCode: firstNullableString(item.pnrCode),
    productMode: firstString(item.productMode),
    status: firstString(item.status),
    customerName: firstNullableString(item.customerName),
    customerEmail: firstNullableString(item.customerEmail),
    updatedAt: firstString(item.updatedAt, item.createdAt),
  };
};

export type IntakeCategory =
  | 'PRIVATE_BALLOON'
  | 'SHARED_BALLOON'
  | 'BUSH_LUNCH'
  | 'CONCIERGE_VIP'
  | 'TRANSFERS'
  | 'LODGE_CAMP'
  | 'PAYMENTS'
  | 'OPERATIONS'
  | 'GENERAL_INQUIRY'
  | 'NEEDS_REVIEW'
  | 'SPAM'
  | 'CHARTER'
  | 'SCHEDULED'
  | 'CONCIERGE'
  | 'NON_FLIGHT'
  | 'UNCERTAIN';
export type IntakeSlaLevel = 'GREEN' | 'AMBER' | 'RED';
export type IntakeCaseState = 'OPEN' | 'WAITING_CLIENT' | 'RESOLVED_WON' | 'RESOLVED_LOST' | 'NO_ACTION';

export type IntakeQueueItem = {
  id: string;
  threadId: string;
  customerId?: string | null;
  contactName?: string | null;
  contactCompany?: string | null;
  contactMatchConfidence?: string | null;
  contactMatchReason?: string | null;
  paymentPolicyType?: string | null;
  paymentPolicyLabel?: string | null;
  creditStatus?: string | null;
  requiresPrepayment?: boolean;
  bookingBlockMode?: string | null;
  paymentPolicyNotes?: string | null;
  paymentWarningLabel?: string | null;
  paymentBadgeTone?: 'GREEN' | 'RED' | 'ORANGE' | 'GRAY';
  paymentBadgeLabel?: string | null;
  contactLinkSuppressed?: boolean;
  contactLinkMode?: string | null;
  participantSummary?: string | null;
  participantLabels?: string[];
  participantDetails?: Array<{
    label: string;
    email?: string | null;
    kind: 'EXTERNAL' | 'INTERNAL' | 'SELF';
    customerId?: string | null;
    company?: string | null;
  }>;
  receivedAt: string;
  createdAt: string;
  subject: string;
  fromEmail: string;
  toEmails: string;
  category: IntakeCategory;
  confidence: number;
  flightRelated: boolean;
  reasons: string[];
  labels?: string[];
  inboxKey: string;
  inboxKeys?: string[];
  isWatched: boolean;
  hasReply: boolean;
  agentDraftEligible: boolean;
  linkedQuoteId?: string | null;
  linkedQuoteNumber?: string | null;
  linkedQuoteMode?: string | null;
  linkedQuoteStatus?: string | null;
  convertedAt?: string | null;
  convertedByName?: string | null;
  convertedByEmail?: string | null;
  repliedAt?: string | null;
  repliedBy?: string | null;
  replySource?: string | null;
  replyDeliveryStatus?: string | null;
  replyDeliveryAt?: string | null;
  replyDeliveryError?: string | null;
  assignedToUserId?: string | null;
  assignedToName?: string | null;
  assignedToEmail?: string | null;
  assignedAt?: string | null;
  assignedByName?: string | null;
  caseState?: IntakeCaseState | null;
  archived?: boolean;
  gmailState?: string | null;
  gmailDeletedAt?: string | null;
  gmailMissingAt?: string | null;
  verificationStatus?: 'UNVERIFIED' | 'TRUSTED' | 'VERIFIED' | string | null;
  verificationAt?: string | null;
  verifiedByName?: string | null;
  verifiedByEmail?: string | null;
  slaAgeMinutes?: number | null;
  slaLevel?: IntakeSlaLevel | null;
  isRead?: boolean;
  readByUsers?: { userId: string; userName: string | null; readAt: string }[];
  attachmentCount?: number;
  noteCount?: number;
  latestNoteAt?: string | null;
  latestNotePreview?: string | null;
  latestNoteAuthor?: string | null;
  unreadNoteCount?: number;
  followUpTaskId?: string | null;
  followUpDueAt?: string | null;
  followUpOwnerUserId?: string | null;
  followUpOwnerName?: string | null;
  followUpNote?: string | null;
  followUpStatus?: 'PENDING' | 'DUE' | 'OVERDUE' | 'FOLLOWED' | string | null;
  followUpCompletedAt?: string | null;
  messageCount?: number;
  unreadMessageCount?: number;
  lastActivity?: { action: string; by: string | null; at: string } | null;
  reconfirmation?: {
    status: string;
    scheduledAt?: string | null;
    flightDate?: string | null;
    firstSentAt?: string | null;
    secondSentAt?: string | null;
    clientRepliedAt?: string | null;
  } | null;
  provisional?: {
    status: string;
    badgeState: string | null;
    firstFollowUpAt: string | null;
    firstActionedAt: string | null;
    secondFollowUpAt: string | null;
    secondActionedAt: string | null;
    clientRepliedAt: string | null;
  } | null;
  provisionalHint?: boolean;
};

export type IntakeWorkflowFilter = 'ALL' | 'FOLLOW_UP' | 'PROVISIONAL' | 'RECONFIRMATION';

export type IntakeThreadNote = {
  id: string;
  userId: string;
  userName: string | null;
  content: string;
  createdAt: string;
  updatedAt: string;
};

export type IntakeThreadNoteSummary = {
  messageId: string;
  threadId: string;
  noteCount: number;
  latestNoteAt: string | null;
  latestNotePreview: string | null;
  latestNoteAuthor: string | null;
  unreadNoteCount: number;
};

export type IntakeRecentThreadNoteItem = {
  messageId: string;
  threadId: string;
  subject: string;
  fromEmail: string;
  noteCount: number;
  latestNoteAt: string | null;
  latestNotePreview: string | null;
  latestNoteAuthor: string | null;
  unreadNoteCount: number;
};

export type IntakeAlertItem = {
  messageId: string;
  threadId: string;
  category: IntakeCategory;
  subject: string;
  fromEmail: string;
  receivedAt: string;
  ageMinutes: number;
  escalationLevel: number;
  notifyRoles: string[];
  notifyEmails: string[];
  channel: 'IN_APP' | 'EMAIL' | 'BOTH';
};

export type IntakeMessageDetail = {
  message: {
    id: string;
    threadId: string;
    customerId?: string | null;
    contactName?: string | null;
    contactCompany?: string | null;
    contactMatchConfidence?: string | null;
    contactMatchReason?: string | null;
    paymentPolicyType?: string | null;
    paymentPolicyLabel?: string | null;
    creditStatus?: string | null;
    requiresPrepayment?: boolean;
    bookingBlockMode?: string | null;
    paymentPolicyNotes?: string | null;
    paymentWarningLabel?: string | null;
    paymentBadgeTone?: 'GREEN' | 'RED' | 'ORANGE' | 'GRAY';
    paymentBadgeLabel?: string | null;
    contactLinkSuppressed?: boolean;
    contactLinkMode?: string | null;
    contactAvatarUrl?: string | null;
    subject: string;
    fromEmail: string;
    toEmails: string;
    ccEmails: string;
    bccEmails: string;
    textBody: string;
    htmlBody: string;
    receivedAt: string;
    rawPayload: any;
    linkedQuoteId?: string | null;
    linkedQuoteNumber?: string | null;
    linkedQuoteMode?: string | null;
    linkedQuoteStatus?: string | null;
    category?: IntakeCategory;
    confidence?: number;
    flightRelated?: boolean;
    reasons?: string[];
    caseState?: IntakeCaseState | null;
    archived?: boolean;
    gmailState?: string | null;
    gmailDeletedAt?: string | null;
    gmailMissingAt?: string | null;
    gmailLastSeenAt?: string | null;
    verificationStatus?: 'UNVERIFIED' | 'TRUSTED' | 'VERIFIED' | string | null;
    verificationAt?: string | null;
    verifiedByName?: string | null;
    verifiedByEmail?: string | null;
    assignedToUserId?: string | null;
    assignedToName?: string | null;
    assignedToEmail?: string | null;
    assignedAt?: string | null;
    assignedByName?: string | null;
    convertedAt?: string | null;
    convertedByName?: string | null;
    convertedByEmail?: string | null;
    repliedAt?: string | null;
    repliedBy?: string | null;
    replySource?: string | null;
    labels?: string[];
    missingFields?: string[];
    missingQuestions?: string[];
    forwardedRouting?: {
      detected: boolean;
      originalSenderEmail?: string | null;
      forwarderEmail?: string | null;
      suggestedCc?: string[];
    };
    replyMailboxOptions?: string[];
    replyMailboxDefaultKey?: string | null;
    followUp?: {
      taskId?: string | null;
      dueAt?: string | null;
      ownerUserId?: string | null;
      ownerName?: string | null;
      note?: string | null;
      status?: 'PENDING' | 'DUE' | 'OVERDUE' | 'FOLLOWED' | string | null;
      completedAt?: string | null;
    } | null;
  };
  thread: {
    id: string;
    customerId?: string | null;
    contactName?: string | null;
    contactCompany?: string | null;
    paymentPolicyType?: string | null;
    paymentPolicyLabel?: string | null;
    creditStatus?: string | null;
    requiresPrepayment?: boolean;
    bookingBlockMode?: string | null;
    paymentPolicyNotes?: string | null;
    paymentWarningLabel?: string | null;
    paymentBadgeTone?: 'GREEN' | 'RED' | 'ORANGE' | 'GRAY';
    paymentBadgeLabel?: string | null;
    contactLinkSuppressed?: boolean;
    contactLinkMode?: string | null;
    contactAvatarUrl?: string | null;
    customerEmail: string;
    status: string;
    assignedToUserId?: string | null;
    assignedToName?: string | null;
    assignedToEmail?: string | null;
    assignedAt?: string | null;
    messages: Array<{
      id: string;
      direction: string;
      status: string;
      sourceLabel?: string;
      repliedBy?: string;
      subject: string;
      fromEmail: string;
      contactAvatarUrl?: string;
      actorPhotoUrl?: string;
      toEmails: string;
      textBody: string;
      htmlBody: string;
      attachments?: Array<{
        attachmentId?: string;
        fileName: string;
        mimeType?: string | null;
        sizeBytes?: number | null;
        url?: string | null;
        canDownload?: boolean;
        contentId?: string | null;
        isInline?: boolean;
      }>;
      createdAt: string;
      receivedAt?: string;
      sentAt?: string;
    }>;
  };
  auditTimeline?: Array<{
    id: string;
    at: string;
    action: string;
    by: string;
    changes?: any;
  }>;
};

export type EmailReplyAction = 'ASK_INFO' | 'SEND_OFFER' | 'FOLLOW_UP' | 'VERIFY_CLIENT' | 'CUSTOM';
export type EmailReplyPolishMode = 'PROFESSIONAL' | 'SHORTER' | 'PREMIUM' | 'GRAMMAR' | 'WARMER';
export type EmailReplyIntentType =
  | 'INFO_REQUEST'
  | 'BOOKING_REQUEST'
  | 'ACTIVE_DEAL'
  | 'FOLLOW_UP'
  | 'CONFIRMATION'
  | 'UNCLEAR';
export const REPLY_SUGGESTION_FEEDBACK_REASON_CODES = [
  'WRONG_TONE',
  'WRONG_INTENT',
  'MISSING_INFO',
  'TOO_LONG',
  'OTHER',
] as const;
export type ReplySuggestionFeedbackReasonCode =
  (typeof REPLY_SUGGESTION_FEEDBACK_REASON_CODES)[number];
export type EmailReplyScenario =
  | 'ANSWER_AND_GUIDE'
  | 'ASK_MISSING_INFO'
  | 'ACKNOWLEDGE_AND_REVIEW'
  | 'SEND_OFFER'
  | 'FOLLOW_UP'
  | 'VERIFY_CLIENT'
  | 'CONFIRM_NEXT_STEP'
  | 'CUSTOM_REPLY';
export type EmailReplyPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type EmailReplyFollowUpState = 'NONE' | 'PENDING' | 'DUE' | 'OVERDUE' | 'FOLLOWED';
export type EmailReplyVerificationState = 'NOT_REQUIRED' | 'UNVERIFIED' | 'TRUSTED' | 'VERIFIED';
export type EmailReplyQuoteState = 'NONE' | 'DRAFT' | 'SENT_WAITING_CLIENT' | 'APPROVED' | 'DECLINED' | 'ACTIVE';

export type EmailReplyDecisionBar = {
  scenario: EmailReplyScenario;
  intentType: EmailReplyIntentType;
  intentLabel: string;
  intentConfidence: number;
  intentExplanation?: string | null;
  missingFields: string[];
  priorityLabel: EmailReplyPriority;
  recommendedAction: EmailReplyAction;
  followUpState: EmailReplyFollowUpState;
  verificationState: EmailReplyVerificationState;
  quoteState: EmailReplyQuoteState;
  explanation: string;
};

export type EmailReplyIntelligenceResponse = {
  decisionBar: EmailReplyDecisionBar;
  suggestedModes: Array<{ key: EmailReplyAction; label: string; recommended: boolean }>;
  suggestionAvailable: boolean;
  copilotEnabled: boolean;
  aiRefineAvailable: boolean;
  reasonUnavailable?: string | null;
  suggestion: {
    subject: string;
    body: string;
    modeUsed: EmailReplyAction;
    source: 'TEMPLATE_ONLY' | 'TEMPLATE_PLUS_AI';
    templateUsed?: {
      key: string | null;
      title: string | null;
      scenario: EmailReplyScenario;
      tone: string | null;
      source: 'SUPERVISOR_TEMPLATE' | 'SYSTEM_FALLBACK';
    };
    sourceComponents?: string[];
    goldExamplesUsed?: boolean;
    goldExampleCount?: number;
    goldExampleTitles?: string[];
    offerReady?: boolean;
    warnings: string[];
    confidence: number;
    suggestionLogId?: string | null;
  } | null;
};

export type EmailReplyPolishResponse = {
  polishedHtml: string;
  polishedText: string;
  warnings: string[];
  logId: string | null;
};

export type IntakeQuoteSearchItem = {
  id: string;
  quoteNumber: string | null;
  bookingId: string | null;
  pnrCode: string | null;
  productMode: string;
  status: string;
  customerName: string | null;
  customerEmail: string | null;
  updatedAt: string;
};

export type IntakeMatchOfferResult = {
  matched: boolean;
  requestId: string;
  inventoryId?: string;
  offerId?: string;
  offerStatus?: string;
  requestStatus?: string;
  route?: string;
  departureAt?: string;
  seatPriceUSD?: number;
  message?: string;
  reason?: string;
};

export type IntakeReplySignatureOption = {
  userId: string;
  label: string;
  email?: string | null;
  isOwn: boolean;
  html?: string;
};

export type ReservationVoucherAttachment = {
  filename: string;
  mimeType: string;
  size?: number;
  attachmentId?: string;
  storageKey?: string;
  previewUrl?: string;
  downloadUrl?: string;
  dataBase64?: string;
};

export type ReservationDocumentKind = 'voucher' | 'invoice';

export type ReservationGeneratedDocuments = {
  voucher?: ReservationVoucherAttachment;
  invoice?: ReservationVoucherAttachment;
};

const sanitizeReservationDocumentUrl = (value: unknown): string | undefined => {
  const url = firstNullableString(value)?.trim() || '';
  if (!url) return undefined;
  if (/^cid:/i.test(url)) return undefined;
  return url;
};

const looksLikePdfBase64 = (value: unknown): string | undefined => {
  const raw = firstNullableString(value)?.replace(/\s+/g, '') || '';
  if (!raw) return undefined;
  try {
    const decoded = atob(raw.slice(0, Math.min(raw.length, 512)));
    if (!decoded.includes('%PDF-')) return undefined;
    return raw;
  } catch {
    return undefined;
  }
};

export type IntakeThreadLockState = {
  locked: boolean;
  isMine?: boolean;
  ownerUserId?: string | null;
  ownerName?: string | null;
  note?: string | null;
  expiresAt?: string | null;
};

export type IntakeOutboxItem = {
  id: string;
  status: string;
  subject: string;
  provider: string;
  toEmails: string;
  error?: string | null;
  sentAt?: string | null;
  lastEventAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type IntakeAnalytics = {
  days: number;
  leads: number;
  converted: number;
  won: number;
  avgFirstReplyMinutes: number | null;
};

export type IntakeLabelItem = {
  label: string;
  count: number;
  unreadCount: number;
};

export type InboundAttachmentOptimizationType = 'PDF' | 'IMAGE';
export type InboundAttachmentOptimizationQualityMode = 'LOW' | 'BALANCED' | 'HIGH';
export type InboundAttachmentZipResponse = {
  success: true;
  zipAttachmentId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  dataBase64: string;
  sourceCount: number;
  totalInputSize: number;
};
export type InboundAttachmentOptimizeResponse = {
  success: true;
  optimizedAttachmentId: string;
  originalSize: number;
  optimizedSize: number;
  mimeType: string;
  filename: string;
  dataBase64: string;
  optimizationType: InboundAttachmentOptimizationType;
  qualityMode: InboundAttachmentOptimizationQualityMode;
  tool?: string;
};

export type SentEmailItem = {
  id: string;
  subject: string;
  toEmails: string;
  fromEmail: string;
  ccEmails: string | null;
  status: string;
  sentAt: string | null;
  createdAt: string;
  threadId: string | null;
  messageId: string | null;
  gmailThreadId: string | null;
  provider: string | null;
  error: string | null;
  bodyHtml: string | null;
  inboxKeys?: string[];
  inboxBadge?: string | null;
  debugSent?: {
    logId: string;
    fromEmail: string;
    threadId: string | null;
    canonicalInboxKeys: string[];
    derivedSentMailboxKey: string | null;
    visibleReason: 'THREAD_MEMBERSHIP' | 'FROM_MAILBOX';
  } | null;
  sentByUser: { id: string; firstName: string | null; lastName: string | null; email: string } | null;
};

export type GmailDraftItem = {
  draftId: string;
  messageId: string;
  threadId: string;
  subject: string;
  to: string;
  snippet: string;
  date: string | null;
};

export type InboundBridgeReport = {
  fromDate: string;
  toDate: string;
  inboxKey: string | null;
  health: {
    status: 'OK' | 'STALE' | 'DEAD' | 'UNKNOWN' | string;
    lastStatusAt: string | null;
    lastIngestAt: string | null;
  };
  summary: {
    totalInbound: number;
    outageWindows: number;
    outageMinutes: number;
    messagesInOutageWindows: number;
    autoBackfillTriggered: number;
    autoBackfillSuccess: number;
    autoBackfillFailed: number;
    manualBackfillTriggered: number;
    manualBackfillSuccess: number;
    manualBackfillFailed: number;
  };
  outageWindows: Array<{
    startedAt: string;
    endedAt: string;
    statusAtStart: string;
    autoBackfillTriggeredAt?: string | null;
    autoBackfillStatus?: string | null;
    autoBackfillError?: string | null;
    durationMinutes: number;
  }>;
  autoBackfillRuns: Array<{
    triggeredAt: string;
    fromDate: string;
    toDate: string;
    inboxKey: string;
    status: string;
    source: string;
    mode?: string | null;
    exactSubject?: string | null;
    found?: number | null;
    inserted?: number | null;
    deduped?: number | null;
    failed?: number | null;
    error?: string | null;
  }>;
};

export type AdminInboundReplyTemplate = {
  id: string;
  key: string;
  name: string;
  category: string;
  categoryKey: string | null;
  categoryName: string | null;
  previewText: string;
  bodyHtml: string;
  mailboxKey: string | null;
  emailCategory: string | null;
  isActive: boolean;
  isDefault: boolean;
  suggestionRules: any | null;
  sortOrder: number;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type AdminInboundReplyTemplateCategory = {
  id: string;
  key: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export type ShiftGuardianSettings = {
  enabled: boolean;
  showToOperators: boolean;
  showToAdmins: boolean;
  timezone: string;
  morningBriefEnabled: boolean;
  morningBriefTime: string;
  middayCheckEnabled: boolean;
  middayCheckTime: string;
  endOfDayCheckEnabled: boolean;
  endOfDayCheckTime: string;
  shiftEndTime: string;
  refreshSeconds: number;
  guardianLookbackHours: number;
  mediumRiskAgeMinutes: number;
  highRiskAgeMinutes: number;
  includeUnassigned: boolean;
  operatorsCanSeeAssignedToOthers: boolean;
  gdashActionsEnabled: boolean;
  allowAdminOverride: boolean;
  operatorCanAssignToSelf: boolean;
  operatorCanSetFollowup: boolean;
  operatorCanMarkSafe: boolean;
  maxItems: number;
  aiSuggestionsEnabled: false;
};

export type ShiftGuardianSummaryItem = {
  messageId: string;
  emailMessageId?: string;
  latestInboundMessageId?: string;
  threadId: string;
  subject: string;
  senderName: string | null;
  senderEmail: string | null;
  inboxKey: string | null;
  receivedAt: string;
  ageMinutes: number;
  assignedToUserId: string | null;
  assignedToName: string | null;
  caseState: string;
  triageCategory: string | null;
  reason: 'NO_REPLY_AFTER_INBOUND' | 'OVERDUE' | 'UNASSIGNED' | 'NEEDS_REVIEW';
  whyShown?: string[];
  slaState?: 'SAFE' | 'WATCH' | 'AT_RISK' | 'BREACHED';
  operationalType?: 'CLIENT_WAITING' | 'COMMERCIAL_OPPORTUNITY' | 'INTERNAL_COORDINATION' | 'PAYMENT_OR_ACCOUNTING' | 'MEDIA_OR_MARKETING' | 'TECH_OR_SYSTEM' | 'FYI_REVIEW' | 'UNKNOWN';
  operationalTypeLabel?: string;
  classificationConfidence?: 'LOW' | 'MEDIUM' | 'HIGH';
  routingReason?: string[];
  suggestedOwnerGroup?: 'RESERVATIONS' | 'FLIGHT_PLANNING' | 'ACCOUNTS' | 'SALES' | 'MARKETING' | 'CONCIERGE' | 'OPERATIONS' | 'IT_ADMIN' | 'SUPERVISOR_REVIEW';
  suggestedOwnerGroupLabel?: string;
  resolutionConfidence?: 'NEEDS_ACTION' | 'BEING_HANDLED' | 'SAFE_TO_DEFER' | 'ESCALATE_NOW';
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  riskScore?: number;
  riskFactors?: string[];
  suggestedAction: string;
  isBeingHandled?: boolean;
  handlingState?: 'NONE' | 'VIEWING' | 'REPLYING' | 'DRAFTING' | 'LOCKED' | 'RECENTLY_ACTIVE' | 'WAITING_INTERNAL';
  handlingOperatorId?: string | null;
  handlingOperatorName?: string | null;
  handlingOperatorEmail?: string | null;
  handlingType?: 'composing' | 'locked' | 'reviewing' | 'replying' | 'assigned_recently' | null;
  handlingSince?: string | null;
  handlingAgeMinutes?: number | null;
  handlingLabel?: string | null;
  recentActivityAgeMinutes?: number | null;
  needSupervisor?: boolean;
  needsDepartment?: boolean;
  neededDepartment?: 'ACCOUNTS' | 'MARKETING' | 'CONCIERGE' | 'IT_ADMIN' | 'FLIGHT_PLANNING' | 'RESERVATIONS' | 'OPERATIONS' | null;
  departmentRequestedByName?: string | null;
  departmentRequestedAt?: string | null;
  waitingInternal?: boolean;
  waitingInternalByName?: string | null;
  waitingInternalAt?: string | null;
  coordinationNote?: string | null;
  coordinationNoteByName?: string | null;
  coordinationNoteAt?: string | null;
  lockState?: {
    locked: boolean;
    lockedByUserId: string | null;
    lockedByName: string | null;
    lockedUntil: string | null;
  };
  lastGDashAction?: string | null;
  lastGDashActionByName?: string | null;
  lastGDashActionAt?: string | null;
  followUpDueAt?: string | null;
  safeUntil?: string | null;
  latestInboundAt?: string | null;
  latestOutboundAt?: string | null;
  assignedToCurrentUser?: boolean;
  canCurrentUserAct?: boolean;
  canCurrentUserOverride?: boolean;
};

export type GdashActionType =
  | 'OPEN'
  | 'ASSIGN_TO_ME'
  | 'SET_FOLLOWUP_TOMORROW'
  | 'MARK_SAFE_TOMORROW'
  | 'I_AM_HANDLING'
  | 'NEED_SUPERVISOR'
  | 'NEED_OTHER_DEPARTMENT'
  | 'WAITING_INTERNAL';

export type GdashDepartment = 'ACCOUNTS' | 'MARKETING' | 'CONCIERGE' | 'IT_ADMIN' | 'FLIGHT_PLANNING' | 'RESERVATIONS' | 'OPERATIONS';
export type GdashActionResponse = {
  success: boolean;
  action: GdashActionType;
  lock?: {
    lockedByUserId: string;
    lockedByName: string;
    lockedUntil: string | null;
  };
  itemState?: {
    assignedToUserId: string | null;
    assignedToName: string | null;
    followUpDueAt: string | null;
    safeUntil: string | null;
    lastGDashAction: string | null;
    lastGDashActionByName: string | null;
    lastGDashActionAt: string | null;
  };
  code?: string;
  message?: string;
  lockedByUserId?: string;
  lockedByName?: string;
  lockedUntil?: string | null;
};

export type InboxAssignmentAction = 'ASSIGN_TO_ME' | 'REASSIGN' | 'UNASSIGN';

export type InboxAssignmentResult = {
  messageId: string;
  action: InboxAssignmentAction;
  assigneeUserId: string | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
  assignedAt?: string | null;
  reason?: string | null;
};

export type ShiftGuardianSummaryResponse = {
  enabled: boolean;
  reason?: 'disabled_by_env' | 'disabled_by_admin' | 'not_allowed_for_role';
  debug?: {
    envEnabled: boolean;
    adminSettingEnabled: boolean;
    userRole: string;
    showToOperators: boolean;
    showToAdmins: boolean;
  };
  generatedAt?: string;
  settings?: {
    timezone: string;
    morningBriefEnabled?: boolean;
    morningBriefTime: string;
    middayCheckEnabled?: boolean;
    middayCheckTime: string;
    endOfDayCheckEnabled?: boolean;
    endOfDayCheckTime: string;
    shiftEndTime: string;
    refreshSeconds: number;
    guardianLookbackHours?: number;
    effectiveLookbackHours?: number;
    gdashActionsEnabled?: boolean;
    allowAdminOverride?: boolean;
    operatorCanAssignToSelf?: boolean;
    operatorCanSetFollowup?: boolean;
    operatorCanMarkSafe?: boolean;
  };
  summary?: {
    totalNeedsAttention: number;
    overdue: number;
    unassigned: number;
    noReplyAfterInbound: number;
    highRisk: number;
    mediumRisk: number;
    lowRisk: number;
  };
  items: ShiftGuardianSummaryItem[];
  error?: string;
};

export type GdashReportThread = {
  threadId: string;
  messageId?: string | null;
  subject: string;
  riskLevel: string;
  status: string;
  caseState?: string | null;
  latestCaseState?: string | null;
  latestDirection?: string | null;
  latestInboundAt?: string | null;
  latestOutboundAt?: string | null;
  operatorTouched?: boolean;
  stillOperationallyUnresolved?: boolean;
  assignedToName: string | null;
  lastAction: string;
  lastActionAt: string | null;
  followUpDueAt?: string | null;
};

export type GdashReportOperator = {
  userId: string;
  name: string;
  unknownUserId?: boolean;
  userIdShort?: string;
  role: string;
  lastGdashActionAt?: string | null;
  metrics: {
    opened: number;
    assignedToSelf: number;
    followupsTomorrow: number;
    safeTomorrow: number;
    overrides: number;
    resolvedAfterGdash: number;
    unresolvedAtShiftFinish: number;
    unresolvedAfterInteraction?: number;
    ignoredHighRisk: number;
    noActionYetHighRisk?: number;
    seenButUntouched?: number;
    averageResponseMinutes: number | null;
  };
  activeThreads: GdashReportThread[];
};

export type GdashReportResponse = {
  generatedAt: string;
  timezone: string;
  date: string;
  pendingLabelMode?: 'NOW' | 'SHIFT_FINISH';
  summary: {
    totalOperators: number;
    totalActions: number;
    totalOpened: number;
    totalAssigned: number;
    totalFollowups: number;
    totalSafeTomorrow: number;
    totalOverrides: number;
    highRiskShown?: number;
    unresolvedHighRisk: number;
    unresolvedAfterInteraction?: number;
    seenButUntouched?: number;
    ignoredHighRisk: number;
    noActionYetHighRisk?: number;
  };
  operators: GdashReportOperator[];
};

const normalizeInboundReplyTemplate = (raw: any): InboundReplyTemplate => {
  const item = asObject(raw);
  return {
    id: firstString(item.id) || undefined,
    key: firstString(item.key),
    scope: (firstString(item.scope).toUpperCase() === 'PERSONAL' ? 'PERSONAL' : 'COMPANY'),
    categoryKey: firstNullableString(item.categoryKey),
    categoryName: firstNullableString(item.categoryName),
    isDefault: toBoolean(item.isDefault, false),
    suggestionRules: item.suggestionRules ?? null,
    label: firstString(item.label, item.name),
    category: firstString(item.category).toUpperCase() as any,
    previewName: firstString(item.previewName, item.previewText),
    html: firstString(item.html, item.bodyHtml),
  };
};

const normalizeAdminInboundReplyTemplate = (raw: any): AdminInboundReplyTemplate => {
  const item = asObject(raw);
  return {
    id: firstString(item.id),
    key: firstString(item.key),
    name: firstString(item.name),
    category: firstString(item.category),
    categoryKey: firstNullableString(item.categoryKey),
    categoryName: firstNullableString(item.categoryName),
    previewText: firstString(item.previewText),
    bodyHtml: firstString(item.bodyHtml),
    mailboxKey: firstNullableString(item.mailboxKey),
    emailCategory: firstNullableString(item.emailCategory),
    isActive: toBoolean(item.isActive, true),
    isDefault: toBoolean(item.isDefault, false),
    suggestionRules: item.suggestionRules ?? null,
    sortOrder: toNumberOr(item.sortOrder, 100),
    createdBy: firstNullableString(item.createdBy),
    updatedBy: firstNullableString(item.updatedBy),
    createdAt: firstNullableString(item.createdAt),
    updatedAt: firstNullableString(item.updatedAt),
  };
};

const normalizeAdminInboundReplyTemplateCategory = (raw: any): AdminInboundReplyTemplateCategory => {
  const item = asObject(raw);
  return {
    id: firstString(item.id),
    key: firstString(item.key),
    name: firstString(item.name),
    sortOrder: toNumberOr(item.sortOrder, 100),
    isActive: toBoolean(item.isActive, true),
    createdAt: firstNullableString(item.createdAt),
    updatedAt: firstNullableString(item.updatedAt),
  };
};

export const inboundInboxService = {
  async getInboundReplyTemplates(params?: { mailboxKey?: string; emailCategory?: string }): Promise<InboundReplyTemplate[]> {
    try {
      const response = await api.get('/inbound/reply-templates', {
        params: {
          mailboxKey: params?.mailboxKey || undefined,
          emailCategory: params?.emailCategory || undefined,
        },
      });
      const items = toArray<any>(response.data?.data?.items).map(normalizeInboundReplyTemplate).filter(item => !!item.key && !!item.label);
      if (items.length > 0) return items;
    } catch {
      // fallback below
    }
    return INBOUND_REPLY_TEMPLATES;
  },

  async getAdminInboundReplyTemplates(params?: {
    q?: string;
    category?: string;
    mailboxKey?: string;
    emailCategory?: string;
    isActive?: boolean;
  }): Promise<AdminInboundReplyTemplate[]> {
    const response = await api.get('/admin/inbound/reply-templates', {
      params: {
        q: params?.q || undefined,
        category: params?.category || undefined,
        mailboxKey: params?.mailboxKey || undefined,
        emailCategory: params?.emailCategory || undefined,
        isActive: typeof params?.isActive === 'boolean' ? String(params.isActive) : undefined,
      },
    });
    return toArray<any>(response.data?.data?.items).map(normalizeAdminInboundReplyTemplate);
  },

  async createAdminInboundReplyTemplate(payload: {
    key: string;
    name: string;
    category?: string;
    categoryKey?: string | null;
    previewText: string;
    bodyHtml: string;
    mailboxKey?: string | null;
    emailCategory?: string | null;
    isActive?: boolean;
    isDefault?: boolean;
    suggestionRules?: any;
    sortOrder?: number;
  }): Promise<AdminInboundReplyTemplate> {
    const response = await api.post('/admin/inbound/reply-templates', payload);
    return normalizeAdminInboundReplyTemplate(response.data?.data || {});
  },

  async updateAdminInboundReplyTemplate(id: string, payload: {
    name?: string;
    category?: string;
    categoryKey?: string | null;
    previewText?: string;
    bodyHtml?: string;
    mailboxKey?: string | null;
    emailCategory?: string | null;
    isActive?: boolean;
    isDefault?: boolean;
    suggestionRules?: any;
    sortOrder?: number;
  }): Promise<AdminInboundReplyTemplate> {
    const response = await api.patch(`/admin/inbound/reply-templates/${encodeURIComponent(id)}`, payload);
    return normalizeAdminInboundReplyTemplate(response.data?.data || {});
  },

  async createPersonalInboundReplyTemplate(payload: {
    name: string;
    category?: string;
    categoryKey?: string | null;
    previewText: string;
    bodyHtml: string;
    mailboxKey?: string | null;
    emailCategory?: string | null;
    isActive?: boolean;
    isDefault?: boolean;
    suggestionRules?: any;
    sortOrder?: number;
  }): Promise<InboundReplyTemplate> {
    const response = await api.post('/inbound/reply-templates/personal', payload);
    return normalizeInboundReplyTemplate(response.data?.data || {});
  },

  async updatePersonalInboundReplyTemplate(id: string, payload: {
    name?: string;
    category?: string;
    categoryKey?: string | null;
    previewText?: string;
    bodyHtml?: string;
    mailboxKey?: string | null;
    emailCategory?: string | null;
    isActive?: boolean;
    isDefault?: boolean;
    suggestionRules?: any;
    sortOrder?: number;
  }): Promise<InboundReplyTemplate> {
    const response = await api.patch(`/inbound/reply-templates/personal/${encodeURIComponent(id)}`, payload);
    return normalizeInboundReplyTemplate(response.data?.data || {});
  },

  async getAdminInboundReplyTemplateCategories(): Promise<AdminInboundReplyTemplateCategory[]> {
    const response = await api.get('/admin/inbound/reply-template-categories');
    return toArray<any>(response.data?.data?.items).map(normalizeAdminInboundReplyTemplateCategory);
  },

  async createAdminInboundReplyTemplateCategory(payload: {
    key: string;
    name: string;
    sortOrder?: number;
    isActive?: boolean;
  }): Promise<AdminInboundReplyTemplateCategory> {
    const response = await api.post('/admin/inbound/reply-template-categories', payload);
    return normalizeAdminInboundReplyTemplateCategory(response.data?.data || {});
  },

  async updateAdminInboundReplyTemplateCategory(id: string, payload: {
    name?: string;
    sortOrder?: number;
    isActive?: boolean;
  }): Promise<AdminInboundReplyTemplateCategory> {
    const response = await api.patch(`/admin/inbound/reply-template-categories/${encodeURIComponent(id)}`, payload);
    return normalizeAdminInboundReplyTemplateCategory(response.data?.data || {});
  },

  async getGdashSettings(): Promise<ShiftGuardianSettings> {
    const response = await api.get('/admin/gdash/settings');
    const value = asObject(response.data?.data?.value);
    return {
      enabled: toBoolean(value.enabled, false),
      showToOperators: toBoolean(value.showToOperators, true),
      showToAdmins: toBoolean(value.showToAdmins, true),
      timezone: firstString(value.timezone, 'Africa/Dar_es_Salaam'),
      morningBriefEnabled: toBoolean(value.morningBriefEnabled, true),
      morningBriefTime: firstString(value.morningBriefTime, '08:00'),
      middayCheckEnabled: toBoolean(value.middayCheckEnabled, true),
      middayCheckTime: firstString(value.middayCheckTime, '13:00'),
      endOfDayCheckEnabled: toBoolean(value.endOfDayCheckEnabled, true),
      endOfDayCheckTime: firstString(value.endOfDayCheckTime, '17:30'),
      shiftEndTime: firstString(value.shiftEndTime, '18:00'),
      refreshSeconds: toNumberOr(value.refreshSeconds, 60),
      guardianLookbackHours: toNumberOr(value.guardianLookbackHours, 24),
      mediumRiskAgeMinutes: toNumberOr(value.mediumRiskAgeMinutes, 120),
      highRiskAgeMinutes: toNumberOr(value.highRiskAgeMinutes, 240),
      includeUnassigned: toBoolean(value.includeUnassigned, true),
      operatorsCanSeeAssignedToOthers: toBoolean(value.operatorsCanSeeAssignedToOthers, false),
      gdashActionsEnabled: toBoolean(value.gdashActionsEnabled, true),
      allowAdminOverride: toBoolean(value.allowAdminOverride, true),
      operatorCanAssignToSelf: toBoolean(value.operatorCanAssignToSelf, true),
      operatorCanSetFollowup: toBoolean(value.operatorCanSetFollowup, true),
      operatorCanMarkSafe: toBoolean(value.operatorCanMarkSafe, false),
      maxItems: toNumberOr(value.maxItems, 50),
      aiSuggestionsEnabled: false,
    };
  },

  async updateGdashSettings(payload: Partial<ShiftGuardianSettings>): Promise<ShiftGuardianSettings> {
    const response = await api.patch('/admin/gdash/settings', payload);
    const value = asObject(response.data?.data?.value);
    return {
      enabled: toBoolean(value.enabled, false),
      showToOperators: toBoolean(value.showToOperators, true),
      showToAdmins: toBoolean(value.showToAdmins, true),
      timezone: firstString(value.timezone, 'Africa/Dar_es_Salaam'),
      morningBriefEnabled: toBoolean(value.morningBriefEnabled, true),
      morningBriefTime: firstString(value.morningBriefTime, '08:00'),
      middayCheckEnabled: toBoolean(value.middayCheckEnabled, true),
      middayCheckTime: firstString(value.middayCheckTime, '13:00'),
      endOfDayCheckEnabled: toBoolean(value.endOfDayCheckEnabled, true),
      endOfDayCheckTime: firstString(value.endOfDayCheckTime, '17:30'),
      shiftEndTime: firstString(value.shiftEndTime, '18:00'),
      refreshSeconds: toNumberOr(value.refreshSeconds, 60),
      guardianLookbackHours: toNumberOr(value.guardianLookbackHours, 24),
      mediumRiskAgeMinutes: toNumberOr(value.mediumRiskAgeMinutes, 120),
      highRiskAgeMinutes: toNumberOr(value.highRiskAgeMinutes, 240),
      includeUnassigned: toBoolean(value.includeUnassigned, true),
      operatorsCanSeeAssignedToOthers: toBoolean(value.operatorsCanSeeAssignedToOthers, false),
      gdashActionsEnabled: toBoolean(value.gdashActionsEnabled, true),
      allowAdminOverride: toBoolean(value.allowAdminOverride, true),
      operatorCanAssignToSelf: toBoolean(value.operatorCanAssignToSelf, true),
      operatorCanSetFollowup: toBoolean(value.operatorCanSetFollowup, true),
      operatorCanMarkSafe: toBoolean(value.operatorCanMarkSafe, false),
      maxItems: toNumberOr(value.maxItems, 50),
      aiSuggestionsEnabled: false,
    };
  },

  async getShiftGuardianSummary(params?: { team?: 'all' | 'reservations' | 'flight_planners'; mailboxKey?: string; checkType?: 'morning' | 'midday' | 'eod' | 'finish' | 'preview' }): Promise<ShiftGuardianSummaryResponse> {
    const response = await api.get('/inbound/shift-guardian/summary', {
      params: {
        team: params?.team || undefined,
        mailboxKey: params?.mailboxKey || undefined,
        checkType: params?.checkType || undefined,
      },
    });
    const data = asObject(response.data || {});
    return {
      enabled: toBoolean(data.enabled, false),
      reason: firstString(data.reason) as any,
      debug: data.debug ? {
        envEnabled: toBoolean((data.debug as any).envEnabled, false),
        adminSettingEnabled: toBoolean((data.debug as any).adminSettingEnabled, false),
        userRole: firstString((data.debug as any).userRole),
        showToOperators: toBoolean((data.debug as any).showToOperators, false),
        showToAdmins: toBoolean((data.debug as any).showToAdmins, false),
      } : undefined,
      generatedAt: firstNullableString(data.generatedAt) || undefined,
      settings: data.settings ? {
        timezone: firstString((data.settings as any).timezone, 'Africa/Dar_es_Salaam'),
        morningBriefEnabled: toBoolean((data.settings as any).morningBriefEnabled, true),
        morningBriefTime: firstString((data.settings as any).morningBriefTime, '08:00'),
        middayCheckEnabled: toBoolean((data.settings as any).middayCheckEnabled, true),
        middayCheckTime: firstString((data.settings as any).middayCheckTime, '13:00'),
        endOfDayCheckEnabled: toBoolean((data.settings as any).endOfDayCheckEnabled, true),
        endOfDayCheckTime: firstString((data.settings as any).endOfDayCheckTime, '17:30'),
        shiftEndTime: firstString((data.settings as any).shiftEndTime, '18:00'),
        refreshSeconds: toNumberOr((data.settings as any).refreshSeconds, 60),
        guardianLookbackHours: toNumberOr((data.settings as any).guardianLookbackHours, 24),
        effectiveLookbackHours: toNumberOr((data.settings as any).effectiveLookbackHours, 24),
        gdashActionsEnabled: toBoolean((data.settings as any).gdashActionsEnabled, true),
        allowAdminOverride: toBoolean((data.settings as any).allowAdminOverride, true),
        operatorCanAssignToSelf: toBoolean((data.settings as any).operatorCanAssignToSelf, true),
        operatorCanSetFollowup: toBoolean((data.settings as any).operatorCanSetFollowup, true),
        operatorCanMarkSafe: toBoolean((data.settings as any).operatorCanMarkSafe, false),
      } : undefined,
      summary: data.summary ? {
        totalNeedsAttention: toNumberOr((data.summary as any).totalNeedsAttention, 0),
        overdue: toNumberOr((data.summary as any).overdue, 0),
        unassigned: toNumberOr((data.summary as any).unassigned, 0),
        noReplyAfterInbound: toNumberOr((data.summary as any).noReplyAfterInbound, 0),
        highRisk: toNumberOr((data.summary as any).highRisk, 0),
        mediumRisk: toNumberOr((data.summary as any).mediumRisk, 0),
        lowRisk: toNumberOr((data.summary as any).lowRisk, 0),
      } : undefined,
      items: toArray<any>(data.items).map(item => ({
        messageId: firstString(item.messageId),
        emailMessageId: firstNullableString(item.emailMessageId) || undefined,
        latestInboundMessageId: firstNullableString(item.latestInboundMessageId) || undefined,
        threadId: firstString(item.threadId),
        subject: firstString(item.subject),
        senderName: firstNullableString(item.senderName),
        senderEmail: firstNullableString(item.senderEmail),
        inboxKey: firstNullableString(item.inboxKey),
        receivedAt: firstString(item.receivedAt),
        ageMinutes: toNumberOr(item.ageMinutes, 0),
        assignedToUserId: firstNullableString(item.assignedToUserId),
        assignedToName: firstNullableString(item.assignedToName),
        caseState: firstString(item.caseState),
        triageCategory: firstNullableString(item.triageCategory),
        reason: firstString(item.reason) as any,
        whyShown: toArray<any>(item.whyShown).map(v => String(v || '').trim()).filter(Boolean),
        slaState: (firstNullableString(item.slaState) || undefined) as any,
        operationalType: (firstNullableString(item.operationalType) || undefined) as any,
        operationalTypeLabel: firstNullableString(item.operationalTypeLabel),
        classificationConfidence: (firstNullableString(item.classificationConfidence) || undefined) as any,
        routingReason: toArray<any>(item.routingReason).map(v => String(v || '').trim()).filter(Boolean),
        suggestedOwnerGroup: (firstNullableString(item.suggestedOwnerGroup) || undefined) as any,
        suggestedOwnerGroupLabel: firstNullableString(item.suggestedOwnerGroupLabel),
        resolutionConfidence: (firstNullableString(item.resolutionConfidence) || undefined) as any,
        riskLevel: firstString(item.riskLevel) as any,
        riskScore: toNumberOr(item.riskScore, 0),
        riskFactors: toArray<any>(item.riskFactors).map(v => String(v || '').trim()).filter(Boolean),
        suggestedAction: firstString(item.suggestedAction),
        isBeingHandled: toBoolean(item.isBeingHandled, false),
        handlingState: (firstNullableString(item.handlingState) || undefined) as any,
        handlingOperatorId: firstNullableString(item.handlingOperatorId),
        handlingOperatorName: firstNullableString(item.handlingOperatorName),
        handlingOperatorEmail: firstNullableString(item.handlingOperatorEmail),
        handlingType: firstNullableString(item.handlingType) as any,
        handlingSince: firstNullableString(item.handlingSince),
        handlingAgeMinutes: item.handlingAgeMinutes == null ? null : toNumberOr(item.handlingAgeMinutes, 0),
        handlingLabel: firstNullableString(item.handlingLabel),
        recentActivityAgeMinutes: item.recentActivityAgeMinutes == null ? null : toNumberOr(item.recentActivityAgeMinutes, 0),
        needSupervisor: toBoolean(item.needSupervisor, false),
        needsDepartment: toBoolean(item.needsDepartment, false),
        neededDepartment: (firstNullableString(item.neededDepartment) || null) as any,
        departmentRequestedByName: firstNullableString(item.departmentRequestedByName),
        departmentRequestedAt: firstNullableString(item.departmentRequestedAt),
        waitingInternal: toBoolean(item.waitingInternal, false),
        waitingInternalByName: firstNullableString(item.waitingInternalByName),
        waitingInternalAt: firstNullableString(item.waitingInternalAt),
        coordinationNote: firstNullableString(item.coordinationNote),
        coordinationNoteByName: firstNullableString(item.coordinationNoteByName),
        coordinationNoteAt: firstNullableString(item.coordinationNoteAt),
        lockState: item.lockState ? {
          locked: toBoolean(item.lockState.locked, false),
          lockedByUserId: firstNullableString(item.lockState.lockedByUserId),
          lockedByName: firstNullableString(item.lockState.lockedByName),
          lockedUntil: firstNullableString(item.lockState.lockedUntil),
        } : undefined,
        lastGDashAction: firstNullableString(item.lastGDashAction),
        lastGDashActionByName: firstNullableString(item.lastGDashActionByName),
        lastGDashActionAt: firstNullableString(item.lastGDashActionAt),
        followUpDueAt: firstNullableString(item.followUpDueAt),
        safeUntil: firstNullableString(item.safeUntil),
        latestInboundAt: firstNullableString(item.latestInboundAt),
        latestOutboundAt: firstNullableString(item.latestOutboundAt),
        assignedToCurrentUser: toBoolean(item.assignedToCurrentUser, false),
        canCurrentUserAct: toBoolean(item.canCurrentUserAct, true),
        canCurrentUserOverride: toBoolean(item.canCurrentUserOverride, false),
      })).filter(item => !!item.messageId),
      error: firstNullableString(data.error) || undefined,
    };
  },

  async runGdashAction(payload: {
    messageId: string;
    threadId?: string;
    action: GdashActionType;
    targetDepartment?: GdashDepartment;
    override?: boolean;
  }): Promise<GdashActionResponse> {
    const response = await api.post('/inbound/gdash/action', payload);
    const data = asObject(response.data || {});
    return {
      success: toBoolean(data.success, false),
      action: firstString(data.action) as GdashActionType,
      lock: data.lock ? {
        lockedByUserId: firstString((data.lock as any).lockedByUserId),
        lockedByName: firstString((data.lock as any).lockedByName),
        lockedUntil: firstNullableString((data.lock as any).lockedUntil),
      } : undefined,
      itemState: data.itemState ? {
        assignedToUserId: firstNullableString((data.itemState as any).assignedToUserId),
        assignedToName: firstNullableString((data.itemState as any).assignedToName),
        followUpDueAt: firstNullableString((data.itemState as any).followUpDueAt),
        safeUntil: firstNullableString((data.itemState as any).safeUntil),
        lastGDashAction: firstNullableString((data.itemState as any).lastGDashAction),
        lastGDashActionByName: firstNullableString((data.itemState as any).lastGDashActionByName),
        lastGDashActionAt: firstNullableString((data.itemState as any).lastGDashActionAt),
      } : undefined,
      code: firstNullableString(data.code) || undefined,
      message: firstNullableString(data.message) || undefined,
      lockedByUserId: firstNullableString(data.lockedByUserId) || undefined,
      lockedByName: firstNullableString(data.lockedByName) || undefined,
      lockedUntil: firstNullableString(data.lockedUntil) || undefined,
    };
  },

  async postGdashNote(payload: {
    messageId: string;
    threadId?: string;
    note: string;
  }): Promise<{ success: boolean; coordinationNote: string | null; coordinationNoteByName: string | null; coordinationNoteAt: string | null }> {
    const response = await api.post('/inbound/gdash/note', payload);
    const data = asObject(response.data || {});
    return {
      success: toBoolean(data.success, false),
      coordinationNote: firstNullableString(data.coordinationNote),
      coordinationNoteByName: firstNullableString(data.coordinationNoteByName),
      coordinationNoteAt: firstNullableString(data.coordinationNoteAt),
    };
  },

  async getGdashReport(params?: {
    date?: string;
    timezone?: string;
    team?: 'all' | 'reservations' | 'flight_planners';
    operatorUserId?: string;
    includeDetails?: boolean;
  }): Promise<GdashReportResponse> {
    const response = await api.get('/admin/gdash/report', {
      params: {
        date: params?.date || undefined,
        timezone: params?.timezone || undefined,
        team: params?.team || undefined,
        operatorUserId: params?.operatorUserId || undefined,
        includeDetails: params?.includeDetails ? 'true' : 'false',
      },
    });
    const data = asObject(response.data || {});
    return {
      generatedAt: firstString(data.generatedAt),
      timezone: firstString(data.timezone),
      date: firstString(data.date),
      pendingLabelMode: firstString(data.pendingLabelMode).toUpperCase() === 'NOW' ? 'NOW' : 'SHIFT_FINISH',
      summary: {
        totalOperators: toNumberOr((data.summary as any)?.totalOperators, 0),
        totalActions: toNumberOr((data.summary as any)?.totalActions, 0),
        totalOpened: toNumberOr((data.summary as any)?.totalOpened, 0),
        totalAssigned: toNumberOr((data.summary as any)?.totalAssigned, 0),
        totalFollowups: toNumberOr((data.summary as any)?.totalFollowups, 0),
        totalSafeTomorrow: toNumberOr((data.summary as any)?.totalSafeTomorrow, 0),
        totalOverrides: toNumberOr((data.summary as any)?.totalOverrides, 0),
        highRiskShown: toNumberOr((data.summary as any)?.highRiskShown, 0),
        unresolvedHighRisk: toNumberOr((data.summary as any)?.unresolvedHighRisk, 0),
        ignoredHighRisk: toNumberOr((data.summary as any)?.ignoredHighRisk, 0),
        noActionYetHighRisk: toNumberOr((data.summary as any)?.noActionYetHighRisk, toNumberOr((data.summary as any)?.ignoredHighRisk, 0)),
      },
      operators: toArray<any>(data.operators).map((op) => ({
        userId: firstString(op.userId),
        name: firstString(op.name),
        unknownUserId: toBoolean(op.unknownUserId, false),
        userIdShort: firstNullableString(op.userIdShort) || undefined,
        role: firstString(op.role),
        lastGdashActionAt: firstNullableString(op.lastGdashActionAt),
        metrics: {
          opened: toNumberOr(op.metrics?.opened, 0),
          assignedToSelf: toNumberOr(op.metrics?.assignedToSelf, 0),
          followupsTomorrow: toNumberOr(op.metrics?.followupsTomorrow, 0),
          safeTomorrow: toNumberOr(op.metrics?.safeTomorrow, 0),
          overrides: toNumberOr(op.metrics?.overrides, 0),
          resolvedAfterGdash: toNumberOr(op.metrics?.resolvedAfterGdash, 0),
          unresolvedAtShiftFinish: toNumberOr(op.metrics?.unresolvedAtShiftFinish, 0),
          ignoredHighRisk: toNumberOr(op.metrics?.ignoredHighRisk, 0),
          noActionYetHighRisk: toNumberOr(op.metrics?.noActionYetHighRisk, toNumberOr(op.metrics?.ignoredHighRisk, 0)),
          averageResponseMinutes: op.metrics?.averageResponseMinutes === null || op.metrics?.averageResponseMinutes === undefined
            ? null
            : toNumberOr(op.metrics?.averageResponseMinutes, 0),
        },
        activeThreads: toArray<any>(op.activeThreads).map((t) => ({
          threadId: firstString(t.threadId),
          messageId: firstNullableString(t.messageId),
          subject: firstString(t.subject),
          riskLevel: firstString(t.riskLevel),
          status: firstString(t.status),
          caseState: firstNullableString(t.caseState),
          assignedToName: firstNullableString(t.assignedToName),
          lastAction: firstString(t.lastAction),
          lastActionAt: firstNullableString(t.lastActionAt),
          followUpDueAt: firstNullableString(t.followUpDueAt),
        })),
      })).filter((op) => !!op.userId),
    };
  },

  async getQueue(params?: {
    category?: string;
    inboxKey?: string;
    includeNonFlight?: boolean;
    includeSpam?: boolean;
    unrepliedOnly?: boolean;
    flightOnly?: boolean;
    limit?: number;
    page?: number;
    pageSize?: number;
    q?: string;
    dateFrom?: string;
    dateTo?: string;
    stateFilter?: string;
    workflowFilter?: IntakeWorkflowFilter | string;
    label?: string;
    assignedToUserId?: string;
  }): Promise<{ items: IntakeQueueItem[]; total: number; page?: number; pageSize?: number; totalPages?: number; stats?: { openCount: number; waitingCount: number; assignedCount: number; noReplyCount: number; wonCount: number; lostCount: number; followUpDueCount: number; followUpOverdueCount: number; followUpCount: number; provisionalCount: number; reconfirmationCount: number } }> {
    const response = await api.get('/inbound/intake/queue', { params });
    const data = response.data?.data || {};
    const s = data.stats || {};
    return {
      items: toArray<any>(data.items).map(normalizeQueueItem),
      total: toNumberOr(data.total, 0),
      page: toNumberOr(data.page, 1),
      pageSize: toNumberOr(data.pageSize, 25),
      totalPages: toNumberOr(data.totalPages, 1),
      stats: {
        openCount: toNumberOr(s.openCount, 0),
        waitingCount: toNumberOr(s.waitingCount, 0),
        assignedCount: toNumberOr(s.assignedCount, 0),
        noReplyCount: toNumberOr(s.noReplyCount, 0),
        wonCount: toNumberOr(s.wonCount, 0),
        lostCount: toNumberOr(s.lostCount, 0),
        followUpDueCount: toNumberOr(s.followUpDueCount, 0),
        followUpOverdueCount: toNumberOr(s.followUpOverdueCount, 0),
        followUpCount: toNumberOr(s.followUpCount, 0),
        provisionalCount: toNumberOr(s.provisionalCount, 0),
        reconfirmationCount: toNumberOr(s.reconfirmationCount, 0),
      },
    };
  },

  async getQueueStats(params?: {
    category?: string;
    inboxKey?: string;
    includeNonFlight?: boolean;
    includeSpam?: boolean;
    unrepliedOnly?: boolean;
    flightOnly?: boolean;
    q?: string;
    dateFrom?: string;
    dateTo?: string;
    workflowFilter?: IntakeWorkflowFilter | string;
    assignedToUserId?: string;
  }): Promise<{ total: number; openCount: number; waitingCount: number; assignedCount: number; noReplyCount: number; wonCount: number; lostCount: number; closedCount: number; followUpDueCount: number; followUpOverdueCount: number; followUpCount: number; provisionalCount: number; reconfirmationCount: number; tabCounts: Record<string, number>; inboxCategories: InboxCategoryConfig[]; bridgeHealth: { lastIngestAt: string | null; ageMinutes: number | null; status: 'OK' | 'STALE' | 'DEAD' | 'UNKNOWN' } }> {
    const response = await api.get('/inbound/intake/stats', { params });
    const d = response.data?.data || {};
    const tc = d.tabCounts || {};
    const inboxCategories = setInboxCategoryConfigCache(d.inboxCategories || []);
    return {
      total: toNumberOr(d.total, 0),
      openCount: toNumberOr(d.openCount, 0),
      waitingCount: toNumberOr(d.waitingCount, 0),
      assignedCount: toNumberOr(d.assignedCount, 0),
      noReplyCount: toNumberOr(d.noReplyCount, 0),
      wonCount: toNumberOr(d.wonCount, 0),
      lostCount: toNumberOr(d.lostCount, 0),
      closedCount: toNumberOr(d.closedCount, 0),
      followUpDueCount: toNumberOr(d.followUpDueCount, 0),
      followUpOverdueCount: toNumberOr(d.followUpOverdueCount, 0),
      followUpCount: toNumberOr(d.followUpCount, 0),
      provisionalCount: toNumberOr(d.provisionalCount, 0),
      reconfirmationCount: toNumberOr(d.reconfirmationCount, 0),
      tabCounts: Object.fromEntries(
        Object.entries(tc).map(([key, value]) => [String(key || '').trim().toUpperCase(), toNumberOr(value, 0)])
      ),
      inboxCategories,
      bridgeHealth: {
        lastIngestAt: d.bridgeHealth?.lastIngestAt || null,
        ageMinutes: d.bridgeHealth?.ageMinutes ?? null,
        status: (d.bridgeHealth?.status || 'UNKNOWN') as 'OK' | 'STALE' | 'DEAD' | 'UNKNOWN',
      },
    };
  },

  async repairGmailLabels(args: { hoursBack?: number; dryRun?: boolean }): Promise<{
    hoursBack: number;
    dryRun: boolean;
    summary: { total: number; ok: number; skipped: number; error: number; dryRun: number };
    results: Array<{ maosThreadId: string; gmailThreadId: string; inboxKey: string; status: string; reason?: string }>;
  }> {
    try {
      const params = new URLSearchParams();
      if (args.hoursBack) params.set('hoursBack', String(args.hoursBack));
      if (args.dryRun) params.set('dryRun', 'true');
      const response = await api.post(`/inbound/intake/labels/repair-gmail?${params.toString()}`);
      return response.data?.data;
    } catch (error: any) {
      const message = String(error?.response?.data?.error || '').trim() || String(error?.message || '').trim() || 'Repair failed';
      throw new Error(message);
    }
  },

  async triggerBackfill(args: { fromDate: string; toDate: string; inboxKey?: string; exactSubject?: string }): Promise<{ triggered: boolean }> {
    try {
      const response = await api.post('/admin/inbound/backfill', args);
      return { triggered: !!response.data?.data?.triggered || !!response.data?.triggered };
    } catch (error: any) {
      const message =
        String(error?.response?.data?.error || '').trim() ||
        String(error?.message || '').trim() ||
        'Backfill trigger failed';
      throw new Error(message);
    }
  },

  async importGmailMessage(args: { mailboxKey?: string; gmailMessageId: string }): Promise<{
    mailboxKey: string;
    gmailMessageId: string;
    imported?: boolean;
    deduped?: boolean;
    skipped?: boolean;
    reason?: string | null;
    labels?: string[];
    gmailThreadId?: string | null;
    thread?: { id: string; subject: string } | null;
    message?: {
      id: string;
      subject: string;
      messageId?: string | null;
      rfcMessageId?: string | null;
      gmailMessageId?: string | null;
      gmailThreadId?: string | null;
      receivedAt?: string | null;
    } | null;
  }> {
    try {
      const response = await api.post('/admin/inbound/backfill/gmail-message', args);
      return response.data?.data || {};
    } catch (error: any) {
      const message =
        String(error?.response?.data?.error || '').trim() ||
        String(error?.message || '').trim() ||
        'Single Gmail import failed';
      throw new Error(message);
    }
  },

  async getBridgeReport(params?: { fromDate?: string; toDate?: string; inboxKey?: string }): Promise<InboundBridgeReport> {
    const response = await api.get('/admin/inbound/bridge-report', { params });
    const data = response.data?.data || {};
    const summary = data.summary || {};
    return {
      fromDate: firstString(data.fromDate),
      toDate: firstString(data.toDate),
      inboxKey: firstNullableString(data.inboxKey),
      health: {
        status: firstString(data.health?.status, 'UNKNOWN'),
        lastStatusAt: firstNullableString(data.health?.lastStatusAt),
        lastIngestAt: firstNullableString(data.health?.lastIngestAt),
      },
      summary: {
        totalInbound: toNumberOr(summary.totalInbound, 0),
        outageWindows: toNumberOr(summary.outageWindows, 0),
        outageMinutes: toNumberOr(summary.outageMinutes, 0),
        messagesInOutageWindows: toNumberOr(summary.messagesInOutageWindows, 0),
        autoBackfillTriggered: toNumberOr(summary.autoBackfillTriggered, 0),
        autoBackfillSuccess: toNumberOr(summary.autoBackfillSuccess, 0),
        autoBackfillFailed: toNumberOr(summary.autoBackfillFailed, 0),
        manualBackfillTriggered: toNumberOr(summary.manualBackfillTriggered, 0),
        manualBackfillSuccess: toNumberOr(summary.manualBackfillSuccess, 0),
        manualBackfillFailed: toNumberOr(summary.manualBackfillFailed, 0),
      },
      outageWindows: toArray<any>(data.outageWindows).map((row) => ({
        startedAt: firstString(row.startedAt),
        endedAt: firstString(row.endedAt),
        statusAtStart: firstString(row.statusAtStart),
        autoBackfillTriggeredAt: firstNullableString(row.autoBackfillTriggeredAt),
        autoBackfillStatus: firstNullableString(row.autoBackfillStatus),
        autoBackfillError: firstNullableString(row.autoBackfillError),
        durationMinutes: toNumberOr(row.durationMinutes, 0),
      })),
      autoBackfillRuns: toArray<any>(data.autoBackfillRuns).map((row) => ({
        triggeredAt: firstString(row.triggeredAt),
        fromDate: firstString(row.fromDate),
        toDate: firstString(row.toDate),
        inboxKey: firstString(row.inboxKey),
        status: firstString(row.status),
        source: firstString(row.source, 'AUTO'),
        mode: firstNullableString(row.mode),
        exactSubject: firstNullableString(row.exactSubject),
        found: Number.isFinite(Number(row.found)) ? Number(row.found) : null,
        inserted: Number.isFinite(Number(row.inserted)) ? Number(row.inserted) : null,
        deduped: Number.isFinite(Number(row.deduped)) ? Number(row.deduped) : null,
        failed: Number.isFinite(Number(row.failed)) ? Number(row.failed) : null,
        error: firstNullableString(row.error),
      })),
    };
  },

  async getAlerts(limit = 50): Promise<{ items: IntakeAlertItem[]; total: number }> {
    const response = await api.get('/inbound/intake/alerts', { params: { limit } });
    const data = response.data?.data || {};
    return {
      items: toArray<any>(data.items).map(normalizeAlertItem),
      total: toNumberOr(data.total, 0),
    };
  },

  async getAnalytics(days = 7): Promise<IntakeAnalytics> {
    const response = await api.get('/inbound/intake/analytics', { params: { days } });
    const data = response.data?.data || {};
    return {
      days: toNumberOr(data.days, days),
      leads: toNumberOr(data.leads, 0),
      converted: toNumberOr(data.converted, 0),
      won: toNumberOr(data.won, 0),
      avgFirstReplyMinutes: Number.isFinite(Number(data.avgFirstReplyMinutes)) ? Number(data.avgFirstReplyMinutes) : null,
    };
  },

  async getLabels(limit = 200, mailboxKey?: string): Promise<{ items: IntakeLabelItem[] }> {
    const response = await api.get('/inbound/intake/labels', { params: { limit, mailboxKey: mailboxKey || undefined } });
    const data = response.data?.data || {};
    return {
      items: toArray<any>(data.items).map(item => ({
        label: firstString(item.label),
        count: toNumberOr(item.count, 0),
        unreadCount: toNumberOr(item.unreadCount, 0),
      })).filter(item => !!item.label && !isHiddenGmailSystemStateLabel(item.label)),
    };
  },

  async getMessageDetail(id: string): Promise<IntakeMessageDetail> {
    const response = await api.get(`/inbound/intake/message/${encodeURIComponent(id)}`);
    return normalizeMessageDetail(response.data?.data);
  },

  async linkThreadContact(threadId: string, customerId: string): Promise<{ threadId: string; customerId: string | null; updatedMessages?: number; suppressed?: boolean; customer?: { id: string; name?: string | null; company?: string | null } | null }> {
    const response = await api.patch(`/inbound/intake/thread/${encodeURIComponent(threadId)}/contact-link`, { customerId });
    return response.data?.data;
  },

  async unlinkThreadContact(threadId: string): Promise<{ threadId: string; customerId: string | null; updatedMessages?: number; suppressed?: boolean }> {
    const response = await api.delete(`/inbound/intake/thread/${encodeURIComponent(threadId)}/contact-link`);
    return response.data?.data;
  },

  async getReplyIntelligence(
    id: string,
    params?: {
      mode?: EmailReplyAction;
      aiRefine?: boolean;
      hasOfferAttachment?: boolean;
      hasLinkedOffer?: boolean;
      offerAttachmentNames?: string[];
    },
  ): Promise<EmailReplyIntelligenceResponse> {
    const response = await api.get(`/inbound/intake/reply-intelligence/${encodeURIComponent(id)}`, {
      params: {
        mode: params?.mode,
        aiRefine: params?.aiRefine ? 'true' : undefined,
        hasOfferAttachment: params?.hasOfferAttachment ? 'true' : undefined,
        hasLinkedOffer: params?.hasLinkedOffer ? 'true' : undefined,
        offerAttachmentNames: Array.isArray(params?.offerAttachmentNames) && params.offerAttachmentNames.length
          ? params.offerAttachmentNames.join(',')
          : undefined,
      },
    });
    return normalizeReplyIntelligence(response.data?.data);
  },

  async polishReplyDraft(payload: {
    threadId: string;
    messageId: string;
    mode: EmailReplyPolishMode;
    draftHtml?: string;
    draftText?: string;
  }): Promise<EmailReplyPolishResponse> {
    const response = await api.post('/inbound/intake/polish-reply', payload);
    const data = asObject(response.data?.data);
    return {
      polishedHtml: firstString(data.polishedHtml),
      polishedText: firstString(data.polishedText),
      warnings: toArray<string>(data.warnings).map(v => String(v || '').trim()).filter(Boolean),
      logId: firstNullableString(data.logId),
    };
  },

  async saveReplyPolishFeedback(
    logId: string,
    payload: {
      accepted?: boolean;
      editedAfterPolish?: boolean;
      sentAfterPolish?: boolean;
    },
  ): Promise<{ updated: boolean }> {
    const response = await api.post(`/inbound/intake/polish-reply-feedback/${encodeURIComponent(logId)}`, payload || {});
    return { updated: !!response?.data?.data?.updated };
  },

  async saveReplySuggestionFeedback(
    logId: string,
    payload: {
      feedbackType?: 'GOOD' | 'BAD' | null;
      reasonCode?: ReplySuggestionFeedbackReasonCode | null;
      reasonText?: string | null;
      inserted?: boolean;
      finalSentBody?: string;
    },
  ): Promise<{ updated: boolean }> {
    const response = await api.post(`/inbound/intake/reply-suggestion-feedback/${encodeURIComponent(logId)}`, payload || {});
    return {
      updated: !!response?.data?.data?.updated,
    };
  },

  async resolveInboundMessageIdByThread(threadId: string): Promise<string | null> {
    try {
      const response = await api.get(`/inbound/intake/thread/${encodeURIComponent(threadId)}/inbound-message-id`);
      return response.data?.data?.messageId || null;
    } catch {
      return null;
    }
  },

  async getAttachmentPreviewToken(messageId: string, attachmentId: string): Promise<{
    token: string;
    previewUrl: string;
    googleViewerUrl: string;
    mimeType: string;
    fileName: string;
    expiresInSeconds: number;
  }> {
    const response = await api.post('/inbound/attachments/preview-token', { messageId, attachmentId });
    return response.data;
  },

  async getAttachmentDrivePreview(messageId: string, attachmentId: string): Promise<{
    fileId: string;
    previewUrl: string;
    mimeType: string;
    fileName: string;
    expiresInSeconds: number;
  }> {
    const response = await api.post('/inbound/attachments/drive-preview', { messageId, attachmentId });
    return response.data;
  },

  async generateReservationDocuments(payload: { bookingCode: string }): Promise<ReservationGeneratedDocuments> {
    const bookingCode = firstString(payload?.bookingCode).toUpperCase();
    const response = await api.post('/inbound/intake/voucher/generate', { bookingCode });
    const data = asObject(response.data?.data ?? response.data);
    const normalizeDocument = (source: unknown, kind: ReservationDocumentKind): ReservationVoucherAttachment | undefined => {
      const root = asObject(source);
      const attachment = asObject(root.attachment);
      const preview = asObject(root.preview);
      const file = asObject(root.file);
      const fallbackFileName = `${kind === 'voucher' ? 'Voucher' : 'Invoice'}-${bookingCode || 'reservation'}.pdf`;
      const previewUrl = sanitizeReservationDocumentUrl(
        firstNullableString(
          root.previewUrl,
          preview.previewUrl,
          attachment.previewUrl,
          file.previewUrl,
        ),
      );
      const downloadUrl = sanitizeReservationDocumentUrl(
        firstNullableString(
          root.downloadUrl,
          preview.downloadUrl,
          attachment.downloadUrl,
          file.downloadUrl,
          root.url,
          attachment.url,
          file.url,
        ),
      );
      const dataBase64 = looksLikePdfBase64(
        firstNullableString(
          root.dataBase64,
          root.base64,
          root.contentBase64,
          attachment.dataBase64,
          attachment.base64,
          file.dataBase64,
          file.base64,
        ),
      );
      const explicitFileName = firstString(
        root.filename,
        root.fileName,
        attachment.filename,
        attachment.fileName,
        file.filename,
        file.fileName,
      );
      const filename = firstString(
        explicitFileName,
        fallbackFileName,
      );
      const mimeType = firstString(
        root.mimeType,
        root.contentType,
        attachment.mimeType,
        attachment.contentType,
        file.mimeType,
        file.contentType,
        'application/pdf',
      );
      const hasUsefulData = !!(previewUrl || downloadUrl || dataBase64 || explicitFileName);
      if (!hasUsefulData) return undefined;
      return {
        filename,
        mimeType,
        size: firstNumber(
          root.size,
          root.sizeBytes,
          attachment.size,
          attachment.sizeBytes,
          file.size,
          file.sizeBytes,
        ),
        attachmentId: firstNullableString(
          root.attachmentId,
          attachment.attachmentId,
          file.attachmentId,
        ) || undefined,
        storageKey: firstNullableString(
          root.storageKey,
          attachment.storageKey,
          file.storageKey,
        ) || undefined,
        previewUrl,
        downloadUrl,
        dataBase64,
      };
    };

    return {
      voucher: normalizeDocument(data.voucher ?? data, 'voucher'),
      invoice: normalizeDocument(data.invoice, 'invoice'),
    };
  },

  async generateReservationVoucher(payload: { bookingCode: string }): Promise<ReservationVoucherAttachment> {
    const documents = await this.generateReservationDocuments(payload);
    if (documents.voucher) return documents.voucher;
    throw new Error('Voucher generation failed');
  },

  async downloadMessageAttachment(messageId: string, attachmentId: string): Promise<{ blob: Blob; fileName: string | null; mimeType: string | null }> {
    const response = await api.get(
      `/inbound/intake/message/${encodeURIComponent(messageId)}/attachment/${encodeURIComponent(attachmentId)}`,
      { responseType: 'blob' }
    );
    const mimeType = firstNullableString(response.headers?.['content-type']);
    const disposition = firstString(response.headers?.['content-disposition']);
    const fileNameMatch =
      /filename\*=UTF-8''([^;]+)/i.exec(disposition) ||
      /filename="?([^\";]+)"?/i.exec(disposition);
    const parsedName = fileNameMatch?.[1] ? decodeURIComponent(fileNameMatch[1]) : '';
    return {
      blob: response.data instanceof Blob ? response.data : new Blob([response.data], { type: mimeType || undefined }),
      fileName: parsedName || null,
      mimeType,
    };
  },

  async getThreadForwardPackage(threadId: string): Promise<{ threadId: string; messageCount: number; messages: any[] }> {
    const response = await api.get(`/inbound/intake/thread/${encodeURIComponent(threadId)}/forward-package`);
    const data = response.data?.data || {};
    return {
      threadId: firstString(data.threadId),
      messageCount: toNumberOr(data.messageCount, 0),
      messages: toArray<any>(data.messages),
    };
  },

  async convertMessage(id: string, target: 'CHARTER' | 'SCHEDULED'): Promise<{ quoteId: string; quoteNumber: string; productMode: string; customerId: string; reused?: boolean }> {
    const response = await api.post(`/inbound/intake/convert/${encodeURIComponent(id)}`, { target });
    return response.data?.data;
  },

  async updateMessageCategory(id: string, category: IntakeCategory): Promise<{ messageId: string; threadId: string; category: IntakeCategory; categoryLabel?: string | null; updatedMessages?: number }> {
    const response = await api.patch(`/inbound/intake/message/${encodeURIComponent(id)}/category`, { category });
    const data = asObject(response.data?.data);
    return {
      messageId: firstString(data.messageId, id),
      threadId: firstString(data.threadId),
      category: normalizeCategory(data.category),
      categoryLabel: firstNullableString(data.categoryLabel),
      updatedMessages: Number.isFinite(Number(data.updatedMessages)) ? Number(data.updatedMessages) : undefined,
    };
  },

  async assignMessage(id: string, assigneeUserId?: string): Promise<InboxAssignmentResult> {
    const response = await api.patch(`/inbound/intake/assign/${encodeURIComponent(id)}`, {
      action: 'ASSIGN_TO_ME',
      assigneeUserId,
    });
    return response.data?.data;
  },

  async reassignMessage(id: string, payload: { assigneeUserId: string; reason: string }): Promise<InboxAssignmentResult> {
    const response = await api.patch(`/inbound/intake/assign/${encodeURIComponent(id)}`, {
      action: 'REASSIGN',
      assigneeUserId: payload.assigneeUserId,
      reason: payload.reason,
    });
    return response.data?.data;
  },

  async unassignMessage(id: string, reason: string): Promise<InboxAssignmentResult> {
    const response = await api.patch(`/inbound/intake/assign/${encodeURIComponent(id)}`, {
      action: 'UNASSIGN',
      reason,
    });
    return response.data?.data;
  },

  async linkMessage(id: string, payload: { quoteId?: string; quoteNumber?: string }): Promise<{ messageId: string; threadId: string; quoteId: string; quoteNumber: string | null; productMode: string; status: string }> {
    const response = await api.patch(`/inbound/intake/link/${encodeURIComponent(id)}`, payload);
    return response.data?.data;
  },

  async searchQuotes(q: string, limit = 10): Promise<{ items: IntakeQuoteSearchItem[]; total: number }> {
    const response = await api.get('/inbound/intake/quotes/search', {
      params: { q, limit },
    });
    const data = response.data?.data || {};
    return {
      items: toArray<any>(data.items).map(normalizeQuoteSearchItem),
      total: toNumberOr(data.total, 0),
    };
  },

  async optimizeAttachment(payload: {
    attachment: { fileName: string; mimeType: string; dataBase64: string; sizeBytes?: number };
    optimizationType: InboundAttachmentOptimizationType;
    qualityMode?: InboundAttachmentOptimizationQualityMode;
  }): Promise<InboundAttachmentOptimizeResponse> {
    const response = await api.post('/inbound/attachments/optimize', payload);
    const data = response.data?.data || response.data || {};
    return {
      success: true,
      optimizedAttachmentId: firstString(data.optimizedAttachmentId),
      originalSize: toNumberOr(data.originalSize, 0),
      optimizedSize: toNumberOr(data.optimizedSize, 0),
      mimeType: firstString(data.mimeType),
      filename: firstString(data.filename),
      dataBase64: firstString(data.dataBase64),
      optimizationType: (String(data.optimizationType || payload.optimizationType).toUpperCase() === 'PDF' ? 'PDF' : 'IMAGE'),
      qualityMode: (['LOW', 'HIGH'].includes(String(data.qualityMode || payload.qualityMode || 'BALANCED').toUpperCase())
        ? String(data.qualityMode || payload.qualityMode || 'BALANCED').toUpperCase()
        : 'BALANCED') as InboundAttachmentOptimizationQualityMode,
      tool: firstNullableString(data.tool),
    };
  },

  async createAttachmentZip(payload: {
    attachments: Array<{ fileName: string; mimeType?: string; dataBase64: string; sizeBytes?: number }>;
  }): Promise<InboundAttachmentZipResponse> {
    const response = await api.post('/inbound/attachments/create-zip', payload);
    const data = response.data?.data || response.data || {};
    return {
      success: true,
      zipAttachmentId: firstString(data.zipAttachmentId),
      fileName: firstString(data.fileName),
      mimeType: firstString(data.mimeType, 'application/zip'),
      sizeBytes: toNumberOr(data.sizeBytes, 0),
      dataBase64: firstString(data.dataBase64),
      sourceCount: toNumberOr(data.sourceCount, payload.attachments.length),
      totalInputSize: toNumberOr(data.totalInputSize, 0),
    };
  },

  async sendReply(id: string, payload: {
    subject?: string;
    text?: string;
    html?: string;
    useForwardedOriginalSender?: boolean;
    includeQuotedHistory?: boolean;
    includeOriginalAttachments?: boolean;
    selectedSignatureUserId?: string;
    attachments?: Array<{ fileName: string; mimeType?: string; dataBase64: string }>;
    to?: string | string[];
    cc?: string | string[];
    bcc?: string | string[];
    sendMode?: 'NEW' | 'SAME_THREAD';
    mailboxKey?: string;
    polishLogId?: string;
  }): Promise<{ messageId: string | null; provider: string; to: string[]; subject: string; warnings?: string[]; includedOriginalAttachmentCount?: number }> {
    const response = await api.post(`/inbound/intake/reply/${encodeURIComponent(id)}`, payload);
    return response.data?.data;
  },

  async getGmailDrafts(mailboxKey?: string): Promise<{ drafts: GmailDraftItem[]; mailboxKey: string }> {
    const params = mailboxKey ? `?mailboxKey=${encodeURIComponent(mailboxKey)}` : '';
    const response = await api.get(`/inbound/intake/gmail-drafts${params}`);
    const data = response.data?.data || {};
    return {
      mailboxKey: String(data.mailboxKey || mailboxKey || 'fly'),
      drafts: toArray<any>(data.drafts).map(d => ({
        draftId: String(d.draftId || ''),
        messageId: String(d.messageId || ''),
        threadId: String(d.threadId || ''),
        subject: String(d.subject || '(no subject)'),
        to: String(d.to || ''),
        snippet: String(d.snippet || ''),
        date: d.date ? String(d.date) : null,
      })),
    };
  },

  async saveGmailDraft(id: string, payload: {
    subject?: string;
    text?: string;
    html?: string;
    selectedSignatureUserId?: string;
    attachments?: Array<{ fileName: string; mimeType?: string; dataBase64: string }>;
    to?: string | string[];
    cc?: string | string[];
    bcc?: string | string[];
    mailboxKey?: string;
  }): Promise<{ draftId: string; mailboxKey: string }> {
    const response = await api.post(`/inbound/intake/reply/${encodeURIComponent(id)}/gmail-draft`, payload);
    return response.data?.data;
  },

  async createReplyFollowUp(id: string, payload: {
    dueAt: string;
    note: string;
    assignMode?: 'ME' | 'KEEP_THREAD_OWNER';
  }): Promise<{ id: string; threadId: string; dueAt: string; assignedToUserId: string | null; assignedToName: string | null; note?: string | null }> {
    const response = await api.post(`/inbound/intake/follow-up/${encodeURIComponent(id)}`, payload);
    return response.data?.data;
  },

  async completeLatestReplyFollowUp(id: string): Promise<{ completed: boolean; taskId?: string; completedAt?: string }> {
    const response = await api.post(`/inbound/intake/follow-up/${encodeURIComponent(id)}/complete`);
    return response.data?.data || { completed: false };
  },

  async getReplySignatures(): Promise<{ selectedSignatureUserId: string | null; options: IntakeReplySignatureOption[] }> {
    const response = await api.get('/inbound/intake/reply-signatures');
    const data = response.data?.data || {};
    return {
      selectedSignatureUserId: firstNullableString(data.selectedSignatureUserId),
      options: toArray<any>(data.options).map(item => ({
        userId: firstString(item.userId, item.id),
        label: firstString(item.label, item.name, item.email, item.userId),
        email: firstNullableString(item.email),
        isOwn: toBoolean(item.isOwn, false),
        html: String(item.html || '').trim() || undefined,
      })).filter(item => !!item.userId),
    };
  },

  async getThreadLock(messageId: string): Promise<IntakeThreadLockState> {
    const response = await api.get(`/inbound/intake/thread-lock/${encodeURIComponent(messageId)}`);
    const data = response.data?.data || {};
    return {
      locked: toBoolean(data.locked, false),
      isMine: toBoolean(data.isMine, false),
      ownerUserId: firstNullableString(data.ownerUserId),
      ownerName: firstNullableString(data.ownerName),
      note: firstNullableString(data.note),
      expiresAt: firstNullableString(data.expiresAt),
    };
  },

  async setThreadLock(messageId: string, payload: { action: 'CLAIM' | 'RELEASE' | 'TAKEOVER'; note?: string }): Promise<IntakeThreadLockState> {
    let raw: any = {};
    try {
      const response = await api.post(`/inbound/intake/thread-lock/${encodeURIComponent(messageId)}`, payload);
      raw = response.data?.data || {};
    } catch (err: any) {
      // 409 means locked by someone else — the response body still has the lock state
      if (err?.response?.status === 409) {
        raw = err.response.data?.data || {};
      } else {
        throw err;
      }
    }
    return {
      locked: toBoolean(raw.locked, false),
      isMine: toBoolean(raw.isMine, false),
      ownerUserId: firstNullableString(raw.ownerUserId),
      ownerName: firstNullableString(raw.ownerName),
      note: firstNullableString(raw.note),
      expiresAt: firstNullableString(raw.expiresAt),
    };
  },

  async getThreadDraft(messageId: string): Promise<{ subject: string; html: string; attachments: Array<{ fileName: string; mimeType?: string; dataBase64?: string; sizeBytes?: number }>; selectedSignatureUserId?: string | null } | null> {
    const response = await api.get(`/inbound/intake/draft/${encodeURIComponent(messageId)}`);
    const data = response.data?.data;
    if (!data) return null;
    return {
      subject: firstString(data.subject),
      html: firstString(data.html),
      attachments: toArray<any>(data.attachments).map(item => ({
        fileName: firstString(item.fileName),
        mimeType: firstNullableString(item.mimeType) || undefined,
        dataBase64: firstNullableString(item.dataBase64) || undefined,
        sizeBytes: Number(item.sizeBytes || 0) || 0,
      })).filter(item => !!item.fileName),
      selectedSignatureUserId: firstNullableString(data.selectedSignatureUserId),
    };
  },

  async saveThreadDraft(messageId: string, payload: { subject?: string; html?: string; attachments?: Array<{ fileName: string; mimeType?: string; dataBase64?: string; sizeBytes?: number }>; selectedSignatureUserId?: string }): Promise<void> {
    await api.post(`/inbound/intake/draft/${encodeURIComponent(messageId)}`, payload);
  },

  async getOutbox(messageId: string): Promise<{ items: IntakeOutboxItem[] }> {
    const response = await api.get(`/inbound/intake/outbox/${encodeURIComponent(messageId)}`);
    const data = response.data?.data || {};
    return {
      items: toArray<any>(data.items).map(item => ({
        id: firstString(item.id),
        status: firstString(item.status),
        subject: firstString(item.subject),
        provider: firstString(item.provider),
        toEmails: firstString(item.toEmails),
        error: firstNullableString(item.error),
        sentAt: firstNullableString(item.sentAt),
        lastEventAt: firstNullableString(item.lastEventAt),
        createdAt: firstString(item.createdAt),
        updatedAt: firstString(item.updatedAt),
      })).filter(item => !!item.id),
    };
  },

  async verifyMessageLead(id: string, payload?: { contactName?: string; companyName?: string; phone?: string }): Promise<{ quoteId: string; quoteNumber: string | null; customerId: string; zohoContactId: string; verificationStatus: string }> {
    const response = await api.post(`/inbound/intake/verify/${encodeURIComponent(id)}`, payload || {});
    return response.data?.data;
  },

  async setCaseState(id: string, state: IntakeCaseState): Promise<{ messageId: string; threadId: string; caseState: IntakeCaseState; slaPaused: boolean }> {
    const response = await api.post(`/inbound/intake/case/${encodeURIComponent(id)}`, { state });
    return response.data?.data;
  },

  async pushThreadLabels(id: string, payload: { addLabels?: string[]; removeLabels?: string[]; syncToGmail?: boolean }): Promise<{ threadId: string; labels: string[]; updatedMessages: number; pushed: boolean; pushError?: string | null; protectedAddBlocked?: boolean; blockedProtectedAddLabels?: string[]; protectedRemoveBlocked?: boolean; blockedProtectedRemoveLabels?: string[] }> {
    const response = await api.post(`/inbound/intake/label-sync/push/${encodeURIComponent(id)}`, payload);
    return response.data?.data;
  },

  async matchAndOffer(id: string): Promise<IntakeMatchOfferResult> {
    const response = await api.post(`/inbound/intake/match-offer/${encodeURIComponent(id)}`);
    return response.data?.data;
  },

  async getNotes(messageId: string): Promise<{ notes: IntakeThreadNote[] }> {
    const response = await api.get(`/inbound/intake/messages/${encodeURIComponent(messageId)}/notes`);
    const data = response.data?.data || {};
    return {
      notes: toArray<any>(data.notes).map(item => ({
        id: firstString(item.id),
        userId: firstString(item.userId),
        userName: firstNullableString(item.userName),
        content: firstString(item.content),
        createdAt: firstString(item.createdAt),
        updatedAt: firstString(item.updatedAt),
      })).filter(n => !!n.id),
    };
  },

  async getNotesSummary(messageIds: string[]): Promise<{ items: IntakeThreadNoteSummary[] }> {
    const cleaned = Array.from(new Set((Array.isArray(messageIds) ? messageIds : []).map(v => String(v || '').trim()).filter(Boolean)));
    if (!cleaned.length) return { items: [] };
    const response = await api.get('/inbound/intake/notes/summary', {
      params: { messageIds: cleaned.join(',') },
    });
    const data = response.data?.data || {};
    return {
      items: toArray<any>(data.items).map(item => ({
        messageId: firstString(item.messageId),
        threadId: firstString(item.threadId),
        noteCount: toNumberOr(item.noteCount, 0),
        latestNoteAt: firstNullableString(item.latestNoteAt),
        latestNotePreview: firstNullableString(item.latestNotePreview),
        latestNoteAuthor: firstNullableString(item.latestNoteAuthor),
        unreadNoteCount: toNumberOr(item.unreadNoteCount, 0),
      })).filter(item => !!item.messageId),
    };
  },

  async getRecentNotes(limit = 40): Promise<{ items: IntakeRecentThreadNoteItem[] }> {
    const response = await api.get('/inbound/intake/notes/recent', {
      params: { limit },
    });
    const data = response.data?.data || {};
    return {
      items: toArray<any>(data.items).map(item => ({
        messageId: firstString(item.messageId),
        threadId: firstString(item.threadId),
        subject: firstString(item.subject),
        fromEmail: firstString(item.fromEmail),
        noteCount: toNumberOr(item.noteCount, 0),
        latestNoteAt: firstNullableString(item.latestNoteAt),
        latestNotePreview: firstNullableString(item.latestNotePreview),
        latestNoteAuthor: firstNullableString(item.latestNoteAuthor),
        unreadNoteCount: toNumberOr(item.unreadNoteCount, 0),
      })).filter(item => !!item.messageId),
    };
  },

  async addNote(messageId: string, content: string): Promise<IntakeThreadNote> {
    const response = await api.post(`/inbound/intake/messages/${encodeURIComponent(messageId)}/notes`, { content });
    const item = response.data?.data?.note || {};
    return {
      id: firstString(item.id),
      userId: firstString(item.userId),
      userName: firstNullableString(item.userName),
      content: firstString(item.content),
      createdAt: firstString(item.createdAt),
      updatedAt: firstString(item.updatedAt),
    };
  },

  async deleteNote(messageId: string, noteId: string): Promise<void> {
    await api.delete(`/inbound/intake/messages/${encodeURIComponent(messageId)}/notes/${encodeURIComponent(noteId)}`);
  },

  async markAsRead(messageId: string): Promise<void> {
    await api.patch(`/inbound/intake/messages/${encodeURIComponent(messageId)}/mark-read`);
  },

  async markAsUnread(messageId: string): Promise<void> {
    await api.patch(`/inbound/intake/messages/${encodeURIComponent(messageId)}/mark-unread`);
  },

  async getSentEmails(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    inboxKey?: string;
    sentBy?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ items: SentEmailItem[]; total: number; page: number; pageSize: number; totalPages: number }> {
    const qs = new URLSearchParams();
    if (params?.page)     qs.set('page',     String(params.page));
    if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params?.search)   qs.set('search',   params.search);
    if (params?.status)   qs.set('status',   params.status);
    if (params?.inboxKey) qs.set('inboxKey', params.inboxKey);
    if (params?.sentBy)   qs.set('sentBy',   params.sentBy);
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo)   qs.set('dateTo',   params.dateTo);
    const response = await api.get(`/inbound/intake/sent?${qs}`);
    const data = response.data?.data || {};
    return {
      items: toArray<any>(data.items).map(item => ({
        id:            firstString(item.id),
        subject:       firstString(item.subject) || '(no subject)',
        toEmails:      firstString(item.toEmails) || '',
        fromEmail:     firstString(item.fromEmail) || '',
        ccEmails:      firstNullableString(item.ccEmails),
        status:        firstString(item.status) || 'UNKNOWN',
        sentAt:        firstNullableString(item.sentAt),
        createdAt:     firstString(item.createdAt),
        threadId:      firstNullableString(item.threadId),
        messageId:     firstNullableString(item.messageId),
        gmailThreadId: firstNullableString(item.gmailThreadId),
        provider:      firstNullableString(item.provider),
        error:         firstNullableString(item.error),
        bodyHtml:      firstNullableString(item.bodyHtml),
        inboxKeys:   toArray<string>(item.inboxKeys).map(v => String(v || '').trim().toLowerCase()).filter(Boolean),
        inboxBadge:  firstNullableString(item.inboxBadge),
        debugSent:   item.debugSent ? {
          logId: firstString(item.debugSent.logId),
          fromEmail: firstString(item.debugSent.fromEmail),
          threadId: firstNullableString(item.debugSent.threadId),
          canonicalInboxKeys: toArray<string>(item.debugSent.canonicalInboxKeys).map(v => String(v || '').trim().toLowerCase()).filter(Boolean),
          derivedSentMailboxKey: firstNullableString(item.debugSent.derivedSentMailboxKey),
          visibleReason: firstString(item.debugSent.visibleReason) as any,
        } : null,
        sentByUser:  item.sentByUser ? {
          id:        firstString(item.sentByUser.id),
          firstName: firstNullableString(item.sentByUser.firstName),
          lastName:  firstNullableString(item.sentByUser.lastName),
          email:     firstString(item.sentByUser.email),
        } : null,
      })),
      total:      Number(data.total) || 0,
      page:       Number(data.page) || 1,
      pageSize:   Number(data.pageSize) || 25,
      totalPages: Number(data.totalPages) || 1,
    };
  },

  async getSentEmailDetail(id: string): Promise<SentEmailItem | null> {
    try {
      const response = await api.get(`/inbound/intake/sent/${encodeURIComponent(id)}`);
      const item = response.data?.data;
      if (!item) return null;
      return {
        id:          firstString(item.id),
        subject:     firstString(item.subject) || '(no subject)',
        toEmails:    firstString(item.toEmails) || '',
        fromEmail:   firstString(item.fromEmail) || '',
        ccEmails:    firstNullableString(item.ccEmails),
        status:      firstString(item.status) || 'UNKNOWN',
        sentAt:      firstNullableString(item.sentAt),
        createdAt:     firstString(item.createdAt),
        threadId:      firstNullableString(item.threadId),
        messageId:     firstNullableString(item.messageId),
        gmailThreadId: firstNullableString(item.gmailThreadId),
        provider:      firstNullableString(item.provider),
        error:         firstNullableString(item.error),
        bodyHtml:      firstNullableString(item.bodyHtml),
        inboxKeys:     toArray<string>(item.inboxKeys).map(v => String(v || '').trim().toLowerCase()).filter(Boolean),
        inboxBadge:    firstNullableString(item.inboxBadge),
        debugSent:   item.debugSent ? {
          logId: firstString(item.debugSent.logId),
          fromEmail: firstString(item.debugSent.fromEmail),
          threadId: firstNullableString(item.debugSent.threadId),
          canonicalInboxKeys: toArray<string>(item.debugSent.canonicalInboxKeys).map(v => String(v || '').trim().toLowerCase()).filter(Boolean),
          derivedSentMailboxKey: firstNullableString(item.debugSent.derivedSentMailboxKey),
          visibleReason: firstString(item.debugSent.visibleReason) as any,
        } : null,
        sentByUser:  item.sentByUser ? {
          id:        firstString(item.sentByUser.id),
          firstName: firstNullableString(item.sentByUser.firstName),
          lastName:  firstNullableString(item.sentByUser.lastName),
          email:     firstString(item.sentByUser.email),
        } : null,
      };
    } catch {
      return null;
    }
  },

  async compose(payload: {
    to: string;
    cc?: string;
    bcc?: string;
    subject: string;
    html: string;
    attachments?: Array<{ fileName: string; mimeType?: string; dataBase64: string }>;
    selectedSignatureUserId?: string;
    inboxKey?: string;
    inReplyTo?: string;
    references?: string;
    gmailThreadId?: string;
  }): Promise<{ logId: string; subject: string; to: string[] }> {
    const response = await api.post('/inbound/intake/compose', payload);
    const data = response.data?.data || {};
    return {
      logId:   firstString(data.logId),
      subject: firstString(data.subject),
      to:      toArray<string>(data.to),
    };
  },

  async getComposeMailboxes(): Promise<{ mailboxes: Array<{ key: string; label: string; email: string | null }>; inboxCategories: InboxCategoryConfig[] }> {
    const response = await api.get('/inbound/intake/compose-mailboxes');
    const data = response.data?.data || {};
    return {
      mailboxes: (data.mailboxes as any[]) || [],
      inboxCategories: setInboxCategoryConfigCache(data.inboxCategories || []),
    };
  },
};
